/* ============================================================
   Venerabull assistant — the brain.

   Deliberately separate from the interface and from the knowledge. Three
   parts, three files, replaceable one at a time:

     vb-knowledge.json      what she knows        <- the asset
     vb-assistant-brain.js  how she finds it      <- this file, disposable
     vb-assistant-*.liquid  how she says it       <- the interface

   This matcher is the cheapest thing that can honestly answer from the
   knowledge file. When a real model sits behind her, the same file becomes its
   retrieval context and this file is deleted. Nothing of value is lost, which
   is the point of building it in this order.

   Rules it will not break:
   - It never invents a figure. Every number it gives came out of the file.
   - It never volunteers history. History surfaces only when the question asks.
   - When it is not confident, it returns null and she says she does not know.
     A wrong answer about a doorway costs a customer a freight return; silence
     costs a phone call.
   ============================================================ */
window.VB_BRAIN = (function () {
  'use strict';

  var KB = null;
  var loading = null;

  /* ---------- language ----------
     norm, words, digitise, expandRanges and measures now live in
     assets/vb-lang.js so the prediction row and the results page read a
     figure the same way she does. She degrades rather than breaks without
     it: no measures means no constraint verdicts, and nothing else changes. */
  var L = (typeof VB_LANG !== 'undefined' && VB_LANG) ? VB_LANG
        : (typeof require === 'function' ? require('./vb-lang.js') : null);
  function norm(s) { return L ? L.norm(s) : (' ' + String(s).toLowerCase() + ' '); }
  function words(s) { return L ? L.words(s) : String(s).toLowerCase().split(/[^a-z0-9']+/).filter(Boolean); }
  function measures(s) { return L ? L.measures(s) : []; }


  /* She holds the subject of the conversation. Without this every question is
     answered cold, and her own suggested follow-ups fail - "my room width is
     10 ft" names no category, because the category was established two lines
     earlier. Held deliberately narrowly: the last subject is only reused when
     the new question is plausibly a continuation about the same thing, which
     means it carries a figure or names one of that category's constraints.
     "What is the capital of France" does not inherit saunas. */
  var LAST = null;

  /* The question she is waiting on an answer to.

     Asking "what ceiling do I need?" and being told "give me the figure" is
     the most natural way anyone uses her, and until now the reply went
     nowhere: a bare "9 foot" matched no category, no constraint and no
     explainer, so she said nothing. She holds the pending constraint until it
     is answered or the subject changes. */
  var PEND = null;
  /* A figure given before there was anything to measure it against.

     "My ceiling is nine and a half feet" - then, next line, "golf simulator".
     She had the number and then threw it away, and asked for it again. People
     do not repeat themselves; they assume you were listening. So a measurement
     that found no verdict is HELD, with the sentence it arrived in, and the
     sentence is what says which constraint it was about when a category
     finally turns up. Cleared the moment it is spent or contradicted. */
  var HELD = null;

  /* ---------- what she has been told ----------

     HELD is one figure looking for a home. NOTED is the whole conversation:
     every figure the customer has given, under the constraint it settled, for
     as long as they are here.

     It exists because people do not repeat themselves and should not have to.
     Somebody who has told her the doorway is 28 inches has told her; asking
     again three questions later is the single clearest sign that nobody is
     listening. It also means a figure given about one category is not thrown
     away when they change their mind and ask about another - a 7 ft ceiling is
     7 ft whether you are asking about saunas or car lifts.

     Kept in memory for the length of the conversation and nowhere else. No
     storage, no network, nothing about who they are - only what they said
     about their room. */
  var NOTED = { figures: Object.create(null), order: [] };
  /* The supply as the customer described it (1 Nov 2026): volts, amps, and
     whether they said three phase or a standard outlet. Amps a constraint has
     taken into NOTED come first; the amps in the customer's own words stand in
     when no constraint took them (a turn naming a doorway and a circuit at
     once, or "supply" read as the water supply). Volts and these words have
     no constraint of their own, and the fit engine needs them to say anything
     about a circuit. */
  var SUPPLY = { v: null, a: null, three: false, outlet: false };

  /* ---------- where it is going, and who is asking ----------

     A figure is not the only thing somebody tells you. "I am putting a sauna
     in the basement" contains a sauna, and a basement, and the basement is
     doing real work: it changes the delivery problem, the moisture problem and
     the electrical problem, and a person who knows this equipment would have
     said so. She was throwing it away.

     Two things are kept, both from the customer's own words and neither
     inferred:

       SETTING - the place they named. Basement, garage, upstairs, outdoors,
       apartment, restaurant, shop floor. Used to add the sentence a person
       would add, and never to change a verdict: the figures decide, the
       setting only decides what is worth mentioning alongside them.

       TRADE - whether this is somebody buying for a business. "My client",
       "we install", "for our restaurant" are people telling you they do not
       need the explanation aimed at a first-time buyer, and talking down to a
       contractor is its own kind of rudeness.

     Nothing here is stored. It lives for the conversation, like everything
     else she remembers. */
  var SETTING = null, TRADE = false;
  var PLACES = [
    ['basement', 'a basement', ['basement', 'cellar', 'lower level', 'below grade']],
    ['garage', 'a garage', ['garage', 'shop', 'workshop', 'bay']],
    ['upstairs', 'upstairs', ['upstairs', 'second floor', 'first floor flat', 'loft', 'attic', 'above the garage']],
    ['outdoors', 'outdoors', ['outdoors', 'outside', 'backyard', 'back yard', 'garden', 'patio', 'deck', 'yard', 'poolside']],
    ['apartment', 'an apartment', ['apartment', 'condo', 'flat', 'high rise', 'walk up']],
    ['commercial', 'a commercial space', ['restaurant', 'cafe', 'coffee shop', 'bar', 'salon', 'barbershop',
                                          'clinic', 'gym floor', 'warehouse', 'store', 'shop floor', 'kitchen']],
    ['cabin', 'a cabin or rental', ['cabin', 'cottage', 'rental', 'airbnb', 'lodge', 'bnb']]
  ];
  var TRADE_CUES = [' my client ', ' our client ', ' clients ', ' we install ', ' i install ',
                    ' for a customer ', ' the customer wants ', ' my customer ', ' we fit ',
                    ' our restaurant ', ' our cafe ', ' our shop ', ' our salon ', ' our warehouse ',
                    ' for the business ', ' for my business ', ' we are opening ', ' the contractor ',
                    ' spec it ', ' specify ', ' our build ', ' the job ', ' on site ', ' trade account '];
  function observe(hay) {
    /* The FIRST place named wins, not the last.

       This used to break out of the inner loop only, so the outer loop carried
       on and whichever entry sat lowest in PLACES took the setting. "A sauna in
       the basement of my cabin" became a cabin, "my basement and my garage"
       became a garage, and the useful sentence about basement stairs was
       dropped in both. Whoever is talking said the basement first; that is the
       one they are standing in. */
    var best = null, at = -1;
    for (var i = 0; i < PLACES.length; i++) {
      for (var j = 0; j < PLACES[i][2].length; j++) {
        var w = PLACES[i][2][j];
        var pos = hay.indexOf(' ' + w + ' ');
        if (pos > -1 && !negated(hay, w) && (at === -1 || pos < at)) {
          at = pos; best = { id: PLACES[i][0], said: PLACES[i][1] };
        }
      }
    }
    if (best) { SETTING = best; }
    if (!TRADE && hasAny(hay, TRADE_CUES)) { TRADE = true; }
  }
  /* The sentence a person would add. Written per setting and per constraint
     UNIT, because what a basement changes about a doorway is not what it
     changes about a circuit. Only ever an addition - never a change of
     verdict. */
  var ASIDE = bare({
    basement: { in: 'Basement stairs are usually the real constraint rather than the doorway at the bottom, so measure the tightest turn on the way down.',
                amps: 'Panels are often in the basement, which usually makes the circuit the easy half of this one.',
                ft: 'Basement ceilings are where ducts and beams hide, so measure to the lowest obstruction rather than to the slab above.' },
    garage: { ft: 'In a garage, measure with the door open - an opener track is lower than the ceiling and it is what stops things.',
              amps: 'Garages often have a subpanel already, which can make this cheaper than it first looks.' },
    upstairs: { lb: 'Upstairs, the floor is the question rather than the room. A filled weight over a joisted floor wants a structural opinion, not a guess.',
                in: 'Upstairs, the stairwell and its turn decide this, not the doorway.' },
    outdoors: { amps: 'Outdoors, the run to the unit and the weather rating of everything on it matter as much as the breaker size.' },
    apartment: { in: 'In an apartment the lift is usually the limit - its internal dimensions and its door, not the flat.' },
    commercial: { amps: 'On a commercial supply this is often straightforward, but confirm what is actually spare on the panel rather than what is installed.' }
  });
  function aside(k) {
    if (!SETTING || !k) { return null; }
    var byPlace = ASIDE[SETTING.id];
    return (byPlace && byPlace[k.unit]) || null;
  }
  function note(k, value) {
    if (!k || value === null || value === undefined || isNaN(value)) { return; }
    var key = k.unit + '|' + k.id;
    if (!NOTED.figures[key]) { NOTED.order.push(key); }
    NOTED.figures[key] = { value: value, unit: k.unit, label: k.label, id: k.id };
  }
  /* the figure she already holds for this constraint, if any */
  function recall(k) {
    if (!k) { return null; }
    var exact = NOTED.figures[k.unit + '|' + k.id];
    if (exact) { return exact; }
    /* Nothing under this constraint's own name, but a figure in the same unit
       measuring the same thing counts: a ceiling is a ceiling. Matched on the
       constraint LABEL rather than the id, because two categories name the
       same measurement the same way and give it different ids. */
    var want = String(k.label || '').toLowerCase();
    for (var i = 0; i < NOTED.order.length; i++) {
      var f = NOTED.figures[NOTED.order[i]];
      if (f.unit !== k.unit) { continue; }
      var have = String(f.label || '').toLowerCase();
      if (have === want) { return f; }
      if (have.indexOf(want) > -1 || want.indexOf(have) > -1) { return f; }
      if (sameThing(have, want)) { return f; }
    }
    return null;
  }
  /* The words that name a DIMENSION rather than a thing. Two constraints in the
     same unit that share only one of these are not the same measurement:
     "doorway width" and "room width" are both a width and 28 inches of doorway
     says nothing at all about the room. What makes two labels the same
     measurement is sharing the SUBJECT - electrical, ceiling, slab - and that
     is the only thing this bridges on. */
  var DIMENSION = bare({ width: 1, height: 1, depth: 1, thickness: 1, weight: 1, load: 1,
                    loading: 1, length: 1, size: 1, capacity: 1, rating: 1, volume: 1,
                    area: 1, clearance: 1, temperature: 1, draw: 1, output: 1,
                    the: 1, a: 1, an: 1, of: 1, and: 1, for: 1, at: 1, in: 1, on: 1,
                    when: 1, where: 1, my: 1, its: 1 });
  function subjectWords(label) {
    return String(label || '').toLowerCase().split(/[^a-z]+/)
      .filter(function (w) { return w.length > 2 && !DIMENSION[w]; });
  }
  function sameThing(a, c) {
    var A = subjectWords(a), C = subjectWords(c), i, j;
    if (!A.length || !C.length) { return false; }
    for (i = 0; i < A.length; i++) {
      for (j = 0; j < C.length; j++) {
        if (A[i] === C[j]) { return true; }
        if (L && L.stem && L.stem(A[i]) === L.stem(C[j])) { return true; }
      }
    }
    return false;
  }
  function noted() {
    var out = [];
    for (var i = 0; i < NOTED.order.length; i++) { out.push(NOTED.figures[NOTED.order[i]]); }
    return out;
  }

  /* True when the last answer was about a category she DEDUCED rather than one
     the customer named. */
  var INFERRED = false;

  /* The shelf. She was built to answer about a CATEGORY and to refuse to
     discuss a product, which was right while there was no shelf to discuss.
     There are 765 models now, and "what saunas do you carry" is the most
     obvious question anyone asks an assistant standing in a shop. She reads
     the same light index the search field reads, and ranks it with the same
     scorer, so she and the field can never disagree about what we sell. */
  var SHELF = null;
  function shelf(ix) {
    SHELF = ix || null;
    /* The word-ownership index in nearest() is derived from this shelf. Leaving
       it standing after a new shelf arrives makes the suggestion depend on the
       order the page happened to load things in. */
    if (typeof OWN_RESET === 'function') { OWN_RESET(); }
    return !!SHELF;
  }
  var OWN_RESET = null;
  function FINDER() {
    return (typeof VB_FIND !== 'undefined' && VB_FIND) ? VB_FIND
         : (typeof require === 'function' ? require('./vb-find.js') : null);
  }
  var CATQ = ['what do you carry','do you carry','do you have','do you sell','what do you sell',
              'what have you got','show me','list the','how many','which ones','what models',
              'cheapest','most expensive','dearest','under','from you','in stock','options'];
  function asksShelf(hay) {
    for (var i = 0; i < CATQ.length; i++) { if (hay.indexOf(CATQ[i]) > -1) { return true; } }
    return false;
  }
  function money(n) { return '$' + String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
  function shelfAnswer(text, hay) {
    var F = FINDER();
    if (!F || !SHELF) { return null; }
    var rows = F.predict(SHELF, text, 14);
    var items = [], catRow = null, i;
    for (i = 0; i < rows.length; i++) {
      if (rows[i].kind === 'item') { items.push(rows[i].row); }
      else if (!catRow) { catRow = rows[i].row; }
    }
    if (!items.length && !catRow) { return null; }
    var P = F.parse(text);
    /* A question about what is on the shelf still has to say WHICH shelf.
       "Is it in stock right now" trips the stock cue, names nothing, and was
       being answered with three home gym products chosen by nothing at all.
       Without a category named, or a price to filter on, the products have to
       have been asked for by a real word. */
    if (!catRow && !P.priced && P.lt === null && P.gt === null && !P.sort) {
      var strong = 0;
      var cnames = {};
      for (i = 0; i < SHELF.c.length; i++) { cnames[SHELF.c[i][0]] = SHELF.c[i][1]; }
      for (i = 0; i < items.length; i++) {
        /* name AND aisle as one bag: "two person sauna" is two words from the
           model and one from the shelf it stands on, and it is a real ask. */
        var sn = F.score(items[i][1] + ' ' + (cnames[items[i][2]] || ''), text);
        if (sn > strong) { strong = sn; }
      }
      if (strong < 4) { return null; }
    }
    var name = catRow ? catRow[1] : null;
    var html;
    if (!items.length) {
      html = 'We carry ' + catRow[4] + ' models in ' + name.toLowerCase() +
             '. <a href="/pages/' + catRow[0] + '">The three worth looking at first are on the category page.</a>';
    } else {
      var top = items.slice(0, 3).map(function (it) {
        return '<a href="/pages/' + it[2] + '?p=' + encodeURIComponent(it[0]) + '">' + it[1] + '</a>, ' + money(it[3]);
      });
      var lead = P.sort === 'asc' ? 'The least expensive first' :
                 P.sort === 'desc' ? 'The most expensive first' :
                 (P.lt !== null || P.gt !== null) ? 'These are inside that' : 'These come up first';
      html = lead + ': ' + top.join('; ') + '.';
      if (catRow) {
        html += ' <a href="/pages/' + catRow[0] + '?view=all">See all ' + catRow[4] + ' ' +
                name.toLowerCase() + '.</a>';
      }
      html += ' <span class="vb-asst-defer">Prices are ours; the specification is the manufacturer\u2019s sheet.</span>';
    }
    return { kind: 'shelf', category: catRow ? catRow[0] : (items[0] ? items[0][2] : null), html: html };
  }

  /* ---------- from a verdict to a shelf ----------

     She could tell you that a 20 amp circuit was tight, and she could list
     what we sell, and she could not put those two facts together. Somebody who
     has just been told their circuit is marginal wants one thing: which of
     these actually runs on it.

     The specification is already in the index - every model carries its own
     line of spec text, and "240 v 30 a" is a figure in a unit she understands.
     So she reads each model's requirement in the unit the customer gave her,
     and keeps the ones that clear it.

     Two rules keep this honest:

       1. She only filters on a unit she can actually read off the model. A
          ceiling height is not written on a sauna, so a ceiling figure
          narrows nothing and she says so rather than pretending it did.
       2. A model with no figure in that unit is KEPT, not dropped. Absence of
          a specification is not evidence of a problem, and quietly hiding
          half the shelf because its spec line is written differently would be
          the worst kind of wrong - invisible.
  */
  function itemFigures(row) {
    var ms = measures(String(row[7] || '') + ' ' + String(row[1] || ''));
    var by = {};
    for (var i = 0; i < ms.length; i++) {
      var u = ms[i].unit;
      if (by[u] === undefined || ms[i].value > by[u]) { by[u] = ms[i].value; }
    }
    return by;
  }
  /* ---------- the supply, through the fit engine ----------

     Her "what fits" answer agrees with the category page, the product page
     and the web app about a circuit (1 Nov 2026, his direction: a larger
     breaker or a different voltage must not become a promise that a piece
     clears the room). So she does not compare amps herself any more. She
     hands each model's own Circuit or Supply line, and the supply the
     customer gave in the picker's words, to the fit engine, and takes its
     verdict: yes clears, no is ruled out, ask needs confirming.

     The engine below is a CHARACTER-IDENTICAL copy of inches(), AMPS and
     fitRows() in assets/vb-shop.js and assets/vb-app.js, held so by
     scripts/test/app-run.js. It is never edited here alone: a change to the
     rule moves in every copy, Swift's FitEngine included, with the fixture. */
  function inches(v) {
    var s = String(v == null ? '' : v);
    var ft = /(\d+(?:\.\d+)?)\s*(?:ft|foot|feet|')/i.exec(s);
    var ins = /(\d+(?:\.\d+)?)\s*(?:in|inch|inches|")/i.exec(s);
    var t = 0;
    if (ft) { t += parseFloat(ft[1]) * 12; }
    if (ins) { t += parseFloat(ins[1]); }
    return t || null;
  }
  var AMPS = { '120 V standard outlet': 0, '240 V 20 A': 20, '240 V 30 A': 30,
               '240 V 40 A': 40, '240 V 50 A': 50, 'Three phase': 999 };

  function fitRows(it, room) {
    var a = it.a || {}, out = [];

    /* the doorway */
    var w = inches(a.Width) || inches(a.Depth) || inches(a['Closed height']);
    var wLab = a.Width ? 'Width' : (a.Depth ? 'Depth' : 'Closed height');
    if (room.door && w) {
      var slack = room.door - w;
      out.push({
        k: slack >= 4 ? 'yes' : (slack >= 0 ? 'tight' : 'no'), w: 'the doorway',
        t: 'Through a ' + room.door + " in doorway",
        s: wLab + ' is ' + a[wLab] + '. ' + (slack >= 4
            ? 'Four inches of room or better.'
            : (slack >= 0
              ? 'It goes through with ' + Math.round(slack) + ' in to spare — crated it will not, so it comes off the pallet at the door.'
              : 'It does not, uncrated or otherwise. It would need to come in another way.'))
      });
    } else if (room.door) {
      out.push({ k: 'ask', w: 'the doorway', t: 'Through a ' + room.door + ' in doorway',
        s: 'We do not carry a width for this piece yet. Ask and we confirm it from the manufacturer drawing before anything ships.' });
    }

    /* the ceiling */
    var ch = inches(a.Ceiling) || inches(a['Bay height']);
    if (room.ceiling && ch) {
      out.push({
        k: room.ceiling >= ch ? 'yes' : 'no', w: 'the ceiling',
        t: 'Under a ' + room.ceiling + ' in ceiling',
        s: 'It wants ' + (a.Ceiling || a['Bay height']) + '. ' +
           (room.ceiling >= ch ? 'Yours clears it.' : 'Yours is ' + Math.round(ch - room.ceiling) + ' in short.')
      });
    }

    /* the power */
    if (room.supply) {
      var need = a.Circuit || a.Supply || '';
      var mine = AMPS[room.supply];
      /* a supply this check does not know goes to the rule below, which asks */
      if (need && /hardwired/i.test(need) && !/(\d+)\s*A/.test(need) &&
          (room.supply === 'Not sure yet' || typeof mine === 'number')) {
        /* A hardwired supply with no breaker rating (1 Nov 2026, his
           direction; 506bf70's accepted behaviour, kept to this one case and
           moved in all three engines with the fixture). It is wired in, so a
           standard outlet is never said to run it, "Not sure yet" included. A
           matching voltage alone does not establish that a circuit carries
           it, because the listing gives no amps, and three phase says neither
           the voltage nor the amps. An established mismatch still fails. */
        var hwWired = 'It is wired in, not plugged in, so an electrician makes the connection, and the ' +
                      'manufacturer\'s installation instructions set the circuit it needs.';
        var hwThree = 'Three phase on its own does not say the voltage or the amps available, so an electrician ' +
                      'confirms it at the panel.';
        var hwKind = 'ask', hwSays = hwWired;
        if (room.supply === 'Not sure yet') {
          /* to confirm, wired in */
        } else if (/three phase/i.test(need)) {
          hwKind = mine === 999 ? 'ask' : 'no';
          hwSays = mine === 999 ? hwThree : 'Three phase is not something a house usually has.';
        } else if (/240/.test(need) && !(mine >= 20)) {
          hwKind = 'no';
          hwSays = 'It needs a 240 V circuit adding.';
        } else if (/240/.test(need)) {
          hwSays = mine === 999 ? hwThree
            : 'Yours is ' + room.supply + '. The listing does not give the amps it needs, so the ' +
              'manufacturer\'s installation instructions decide whether yours carries it.';
        }
        out.push({ k: hwKind, w: 'the supply', t: 'On the supply you have', s: 'It runs on ' + need + '. ' + hwSays });
      } else if (need) {
        /* Every other listed circuit or supply (1 Nov 2026, his direction on
           cdf06ae), answered from the listing's own figures and the supply
           the shopper chose, and nothing else. It clears only when the
           supply is what the listing asks for: a 120 V plug-in on a standard
           outlet, or a 240 V circuit of exactly the amps listed, and a
           matching rating is not approval to install. It fails only where a
           figure the shopper gave conflicts with the listing: another
           voltage, or fewer amps than listed. The rest is to confirm: three
           phase on its own (no voltage, no amps), a three-phase listing on a
           circuit of its voltage (a circuit's figures do not say its phase,
           so the branch phase is confirmed), a listing without amps, a
           larger breaker than listed (the listing sets no largest), a 120 V
           piece on a 240 V circuit, a supply this check does not know, and
           gas, which no electrical supply answers. */
        var lead = (a.Circuit ? 'It needs ' : 'It runs on ') + need + '. ';
        var nv = /(\d+)\s*V/.exec(need), na = /(\d+)\s*A/.exec(need);
        var volts = nv ? parseInt(nv[1], 10) : 0, amps = na ? parseInt(na[1], 10) : 0;
        var plug = /plug-in|standard outlet/i.test(need);
        var sk = 'ask', ss;
        if (/gas/i.test(need)) {
          ss = 'An electrical supply does not settle a gas connection; the manufacturer\'s installation ' +
               'instructions set what it needs.';
        } else if (room.supply === 'Not sure yet') {
          ss = plug && volts === 120 && !amps
            ? 'It plugs into a standard outlet, so check there is one where it goes.'
            : 'Worth checking the panel before you order — an electrician can read it in a minute.';
        } else if (typeof mine !== 'number' || !volts) {
          ss = 'This check cannot compare it with the supply you gave, so an electrician confirms it against the listing.';
        } else if (mine === 999) {
          ss = plug && volts === 120 && !amps
            ? 'It plugs into a standard outlet, so check there is one where it goes.'
            : 'Three phase on its own does not say the voltage or the amps available, so an electrician ' +
              'confirms it at the panel.';
        } else if (/three phase/i.test(need)) {
          if (volts !== (mine === 0 ? 120 : 240)) {
            sk = 'no';
            ss = 'It is listed for ' + volts + ' V three phase and yours is ' + (mine === 0 ? '120' : '240') +
                 ' V, so as listed it needs a ' + volts + ' V three-phase supply.';
          } else if (amps && mine > 0 && mine < amps) {
            sk = 'no'; ss = 'Yours is ' + mine + ' A — it would need upgrading.';
          } else {
            ss = 'The voltage matches, but ' + room.supply + ' does not say its phase, so an electrician confirms ' +
                 'the branch phase at the panel.';
          }
        } else if (volts === 120) {
          if (mine !== 0) {
            ss = plug ? 'Yours is ' + room.supply + ', not a standard outlet, so the outlet where it goes needs confirming.'
                      : 'Yours is ' + room.supply + ', a different voltage, so the circuit needs confirming.';
          } else if (plug && !amps) {
            sk = 'yes'; ss = 'A standard outlet runs it.';
          } else if (amps) {
            ss = 'A standard outlet does not say its amps, so an electrician checks its circuit against the ' +
                 amps + ' A listed.';
          } else {
            ss = 'The listing does not say it plugs into a standard outlet or give its amps, so the ' +
                 'manufacturer\'s sheet sets the circuit it needs.';
          }
        } else if (volts !== 240) {
          sk = 'no';
          ss = 'It is listed for ' + volts + ' V and yours is ' + (mine === 0 ? '120' : '240') + ' V, so as listed ' +
               'it needs a ' + volts + ' V supply.';
        } else if (mine === 0) {
          sk = 'no';
          ss = a.Circuit ? 'A standard outlet will not run it; this is a circuit an electrician adds.'
                         : 'It needs a 240 V circuit adding.';
        } else if (!amps) {
          ss = 'Yours is ' + room.supply + '. The listing does not give the amps it needs, so the ' +
               'manufacturer\'s installation instructions decide whether yours carries it.';
        } else if (mine < amps) {
          sk = 'no'; ss = 'Yours is ' + mine + ' A — it would need upgrading.';
        } else if (mine === amps) {
          sk = 'yes';
          ss = 'Your circuit matches the listed rating. The manufacturer\'s installation instructions still apply.';
        } else {
          ss = 'Yours is ' + mine + ' A, more than the ' + amps + ' A it lists, and the listing does not say a ' +
               'larger breaker is allowed, so the manufacturer\'s installation instructions decide.';
        }
        out.push({ k: sk, w: 'the supply', t: 'On the supply you have', s: lead + ss });
      }
    }

    /* the route in

       Never a confirmed fit (26 Oct 2026). A weight on the sheet is not a plan
       for carrying it: with no weight the answer is unresolved and no handling
       advice is invented; with one, the weight is repeated exactly as the sheet
       states it and how it goes up is a question to settle. */
    if (room.stairs && room.stairs !== 'No stairs') {
      var stated = String(a.Weight == null ? '' : a.Weight).trim() !== '';
      var heavy = /1,500|and over|800-1,500/.test(String(a.Weight || ''));
      out.push({
        k: 'ask', w: 'the route in',
        t: room.stairs === 'A full flight' ? 'Up a flight of stairs' : 'Up a few steps',
        s: !stated
          ? 'Our sheet gives no weight for this piece, so its weight and the plan for handling it on the stairs are unconfirmed.'
          : 'It weighs ' + a.Weight + '. ' + (heavy
             ? 'At that weight stairs are a conversation before the truck is booked, not on the day.'
             : 'How it goes up the stairs, and how many people that takes, is a question to settle with us before the truck is booked.')
      });
    }

    /* the slab

       30 Oct 2026, Nasser's direction: a thickness is never an approval.
       The slab entry is the one our sheet gives, repeated exactly as the
       sheet gives it; it is not a verified manufacturer requirement and
       nothing here says it is. It is compared only when it is one
       thickness: a single positive number with one unit and no other
       figure, sign or word, in at most 15 significant digits (13 in feet,
       so twelve times it still fits in 15 and is never rounded up). A room's
       thickness given as text is read the same way. Thinner than the
       sheet's figure is a "no". Meeting it is still unresolved, because a
       thickness does not show whether a slab is suitable. A room's number
       is the person's exact figure only when the app saved it as one
       (slabExact); any other number is a record that may have been rounded
       to the inch, so it is never said to meet the figure, and it is
       thinner only where an inch of rounding could not change that. A
       piece whose sheet gives no slab gets no slab row: nothing is
       invented for it. */
    if (a.Slab != null) {
      var oneThick = function (v) {
        var m = /^\s*(\d+(?:\.\d+)?)\s*(in|inch|inches|"|ft|foot|feet|')\s*$/i.exec(String(v == null ? '' : v));
        var ft = !!m && /^(?:ft|foot|feet|')$/i.test(m[2]);
        if (!m || m[1].replace('.', '').replace(/^0+/, '').length > (ft ? 13 : 15)) { return null; }
        var n = Number((parseFloat(m[1]) * (ft ? 12 : 1)).toPrecision(15));
        return isFinite(n) && n > 0 ? n : null;
      };
      var listed = String(a.Slab).trim();
      var needSlab = oneThick(listed);
      var exact = typeof room.slab === 'string' || room.slabExact === true;
      var mySlab = typeof room.slab === 'number'
        ? (isFinite(room.slab) && room.slab > 0 ? room.slab : null) : oneThick(room.slab);
      var thinner = !!(mySlab && needSlab && (exact ? mySlab < needSlab : mySlab + 1 <= needSlab));
      out.push({
        k: thinner ? 'no' : 'ask', w: 'the slab',
        t: 'On the slab you have',
        s: (listed ? 'Our sheet gives the slab as ' + listed + '. '
                   : 'Our sheet names a slab for this piece but gives no figure. ') +
           (!mySlab
             ? 'No usable slab thickness is given for this room, so whether the slab is suitable is unconfirmed.'
             : (exact ? 'Yours is ' : 'Yours is recorded as ') + mySlab + ' in' + (!needSlab
               ? ', but our sheet does not give one thickness to compare it with, so whether the slab is suitable is unconfirmed.'
               : (thinner
                 ? ', thinner than that figure.'
                 : (exact
                   ? ', which meets that figure, but a thickness alone does not show whether a slab is suitable, so that is unconfirmed.'
                   : ', which may have been rounded when it was saved, so whether it meets that figure is unconfirmed, and so is whether the slab is suitable.'))))
      });
    }
    return out;
  }

  /* The customer's words, read the way the room picker offers them. Only an
     exact match becomes a picker value; anything else stays in the
     customer's own words, which the engine does not know, so it asks. Amps
     with no voltage is not a 240 V circuit until somebody says so. */
  function observeSupply(text, hay) {
    var ms = measures(String(text || ''));
    for (var i = 0; i < ms.length; i++) {
      if (ms[i].unit === 'v') { SUPPLY.v = ms[i].value; }
      if (ms[i].unit === 'amps') { SUPPLY.a = ms[i].value; }
    }
    var h = ' ' + String(hay || '') + ' ';
    /* "I do not have a standard outlet" is not evidence of one (his review of
       02897b2): a negated phrase clears what it names, so it stays unresolved */
    var NOT = '(?:no|not|dont|don t|do not|does not|doesnt|doesn t|without|isnt|isn t|havent|haven t|lack|lacks|never) (?:[a-z]+ ){0,3}';
    var PHASE = '(?:three|3) ?-?phase', OUTLET = '(?:standard|regular|normal|ordinary|wall|household) (?:outlet|socket|plug)';
    if (new RegExp(' ' + NOT + PHASE + ' ').test(h)) { SUPPLY.three = false; }
    else if (new RegExp(' ' + PHASE + ' ').test(h)) { SUPPLY.three = true; }
    if (new RegExp(' ' + NOT + '(?:' + OUTLET + '|plug it in|plugs in) ').test(h)) { SUPPLY.outlet = false; }
    else if (new RegExp(' (?:' + OUTLET + '|plug it in|plugs in) ').test(h)) { SUPPLY.outlet = true; }
  }
  function roomSupply(amps) {
    if (SUPPLY.three) { return 'Three phase'; }
    if (SUPPLY.v === 240 && amps !== null && AMPS['240 V ' + amps + ' A'] !== undefined) { return '240 V ' + amps + ' A'; }
    /* an outlet is the picker's 120 V outlet only when no other voltage was said:
       "208 V standard outlet" stays in the customer's words, which the engine asks about */
    if (SUPPLY.outlet && (SUPPLY.v === null || SUPPLY.v === 120)) { return '120 V standard outlet'; }
    if (amps !== null || SUPPLY.v !== null) {
      return [SUPPLY.v !== null ? SUPPLY.v + ' V' : '', amps !== null ? amps + ' A' : ''].join(' ').trim();
    }
    return null;
  }
  /* A model's own power line, from the shelf's spec text ("Circuit: 240 V 30 A") */
  function powerOf(row) {
    var re = /(?:^| \u00b7 )(Circuit|Supply|Service): ([^\u00b7]+?)(?= \u00b7 |$)/g, m, a = {};
    var spec = String(row[7] || '');
    while ((m = re.exec(spec))) { a[m[1]] = m[2]; }
    return a;
  }
  function fits(cat) {
    if (!SHELF || !SHELF.i) { return null; }
    var all = noted().filter(function (f) {
      /* only figures this category actually measures */
      return (cat.constraints || []).some(function (k) { return k.unit === f.unit; });
    });
    var amp = null;
    var have = all.filter(function (f) { if (f.unit === 'amps') { amp = f; return false; } return true; });
    if (!amp && SUPPLY.a !== null) { amp = { value: SUPPLY.a, unit: 'amps', label: 'electrical supply' }; }
    var measuresAmps = (cat.constraints || []).some(function (k) { return k.unit === 'amps'; });
    var sup = measuresAmps ? roomSupply(amp ? amp.value : null) : null;
    if (!have.length && !sup) { return null; }
    var pool = [], i, j;
    for (i = 0; i < SHELF.i.length; i++) { if (SHELF.i[i][2] === cat.id) { pool.push(SHELF.i[i]); } }
    if (!pool.length) { return null; }
    var used = {}, kept = [], confirm = [], ruled = 0, ruledBySupply = 0;
    for (i = 0; i < pool.length; i++) {
      var f = itemFigures(pool[i]), ok = true, open = false;
      for (j = 0; j < have.length; j++) {
        var need = f[have[j].unit];
        if (need === undefined) { continue; }        /* no figure is not a fault */
        used[have[j].unit] = 1;
        if (need > have[j].value + 1e-9) { ok = false; }
      }
      if (sup) {
        var pw = powerOf(pool[i]), k = null;
        if (pw.Circuit || pw.Supply) {
          var row = fitRows({ a: pw.Circuit ? { Circuit: pw.Circuit } : { Supply: pw.Supply } }, { supply: sup })
            .filter(function (x) { return x.w === 'the supply'; })[0];
          k = row ? row.k : 'ask';
        } else if (pw.Service) {
          k = 'ask';                                   /* the engine does not read a Service line */
        }
        if (k) {
          used.supply = 1;
          if (k === 'no') { if (ok) { ruledBySupply++; } ok = false; }
          else if (k !== 'yes') { open = true; }
        }
      }
      if (!ok) { ruled++; } else if (open) { confirm.push(pool[i]); } else { kept.push(pool[i]); }
    }
    var units = [];
    for (var u in used) { if (Object.prototype.hasOwnProperty.call(used, u)) { units.push(u); } }
    if (!units.length) { return null; }             /* nothing was actually checked */
    var order = function (a, b2) { return (b2[6] - a[6]) || (b2[5] - a[5]) || (a[3] - b2[3]); };
    kept.sort(order); confirm.sort(order);
    var against = have.filter(function (h) { return used[h.unit]; });
    return { kept: kept, confirm: confirm, ruled: ruled, ruledBySupply: ruledBySupply, total: pool.length,
             against: against, supply: used.supply ? sup : null, amp: used.supply ? amp : null };
  }
  function money2(n) { return '$' + String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
  /* What she says about a supply the engine left to confirm: why, and the next step */
  /* The next step after an electrical range: the step that matches a model */
  function ampsStep() {
    return (SUPPLY.v === null && !SUPPLY.three && !SUPPLY.outlet)
      ? 'To match a particular model, tell me the voltage as well and ask what fits.'
      : 'To match a particular model, ask me what fits.';
  }
  function supplyOpen(r) {
    if (r.supply === 'Three phase') {
      return 'Three phase on its own doesn\'t give the voltage or the amps, so an electrician confirms that at the panel.';
    }
    if (AMPS[r.supply] === undefined) {
      return SUPPLY.v === null
        ? 'I have the amps but not the voltage. Tell me whether it is a 240 V circuit or a 120 V standard outlet and I can match them to their listings.'
        : 'I can\'t match that supply to their listings as given, so an electrician confirms it against each one.';
    }
    return 'Their listings don\'t settle it against your supply, so the manufacturer\'s installation instructions decide.';
  }
  function fitsAnswer(cat) {
    var r = fits(cat);
    if (!r) { return null; }
    var said = r.against.map(function (h) { return fig(h.value, h.unit) + ' of ' + String(h.label).replace(/^the\s+/, ''); });
    if (r.supply) {
      said.push(AMPS[r.supply] === undefined
        ? (r.amp ? fig(r.amp.value, r.amp.unit) + ' of ' + String(r.amp.label).replace(/^the\s+/, '') : 'the supply you gave')
        : (r.supply === 'Three phase' ? 'a three-phase supply'
          : (r.supply === '120 V standard outlet' ? 'a standard outlet' : 'a ' + r.supply + ' circuit')));
    }
    var c = r.confirm.length;
    if (!r.kept.length && !c) {
      return { kind: 'fits', category: cat.id, ruled: r.ruled,
               html: 'Against ' + list(said) + ', nothing in ' + cat.label +
                     ' clears it as configured &mdash; all ' + r.total +
                     (r.ruledBySupply ? ' need more than it gives, or a different supply. ' : ' want more. ') +
                     'That is usually a solvable problem rather than a dead end: the supply, the route or the configuration can each move. ' +
                     'Tell me which of those is fixed and I will tell you what is left.',
               follows: followsFor(cat, null) };
    }
    if (!r.kept.length) {
      return { kind: 'fits', category: cat.id, kept: 0, confirm: c, ruled: r.ruled,
               html: 'Against ' + list(said) + ', I can\'t confirm ' + (r.ruled ? c + ' of the ' + r.total : 'any of the ' + r.total) +
                     ' in ' + cat.label + ' yet' + (r.ruled ? ', and ' + r.ruled + ' are ruled out' : '') + '. ' + supplyOpen(r) +
                     ' <span class="vb-asst-defer">' + (KB.deference || '') + '</span>',
               follows: followsFor(cat, null) };
    }
    var top = r.kept.slice(0, 3).map(function (it) {
      return '<a href="/pages/' + it[2] + '?p=' + encodeURIComponent(it[0]) + '">' + it[1] + '</a>, ' + money2(it[3]);
    });
    var tail = (r.supply ? ' Each of those lists your supply exactly; the manufacturer\'s installation instructions still apply.' : '') +
               (r.ruled ? ' That rules out ' + r.ruled + ' of ' + r.total + '.' : '') +
               (c ? ' ' + c + (c === 1 ? ' more needs' : ' more need') + ' confirming. ' + supplyOpen(r) : '') +
               (!r.ruled && !c ? ' Every one of the ' + r.total + ' clears it.' : '');
    return { kind: 'fits', category: cat.id, kept: r.kept.length, confirm: c, ruled: r.ruled,
             html: 'Against ' + list(said) + ', these clear it: ' + top.join('; ') + '.' + tail +
                   ' <span class="vb-asst-defer">' + (KB.deference || '') + '</span>',
             follows: followsFor(cat, null) };
  }
  /* ---------- the whole room, once ----------
     Answers arrive one figure at a time and the customer is holding the running
     total in their head. "Am I okay?" is a request to stop holding it. So she
     runs everything she has been told against everything the category measures,
     says what is settled, what is outstanding, and asks for exactly ONE more
     thing - the next one, not all of them, because a list of four questions is
     a form and she is not a form. */
  function roomSummary(cat) {
    var settled = [], missing = [];
    (cat.constraints || []).forEach(function (k) {
      var f = recall(k);
      if (f) {
        var fb = band(k, f.value);
        if (fb) { settled.push({ k: k, f: f, b: fb }); return; }
      }
      missing.push(k);
    });
    if (!settled.length) { return null; }
    var worst = 'yes';
    settled.forEach(function (h) {
      if (h.b.verdict === 'no') { worst = 'no'; }
      else if (h.b.verdict === 'tight' && worst !== 'no') { worst = 'tight'; }
      else if (h.b.verdict === 'most' && worst === 'yes') { worst = 'most'; }
    });
    var lines = settled.map(function (h) {
      var mark = h.k.unit === 'amps'
               ? (h.b.verdict === 'no' ? 'below the usual range'
                 : h.b.verdict === 'tight' ? 'at the low end of the usual range'
                 : h.b.verdict === 'most' ? 'within much of the usual range' : 'within the usual range')
               : (h.b.verdict === 'no' ? 'rules it out'
                 : h.b.verdict === 'tight' ? 'tight'
                 : h.b.verdict === 'most' ? 'covers most' : 'clears');
      return '<strong>' + capital(h.k.label) + ', ' + fig(h.f.value, h.k.unit) + '</strong> &mdash; ' + mark + '.';
    });
    var tail;
    /* With the circuit in it, a summary is the category's usual figures, not a
       model (his review of 06df4d6): no figure can "settle" an electrical fit
       before a model is matched, so it says what the figures complete and
       gives the step that matches one. Non-electrical summaries are as before. */
    var elec = settled.some(function (h) { return h.k.unit === 'amps'; }) ||
               (missing.length > 0 && missing[0].unit === 'amps');
    if (!missing.length) {
      tail = (elec ? 'That covers every usual figure I measure on ' + cat.label + '. '
                   : 'That is everything I measure on ' + cat.label + '. ') +
             (worst === 'no' ? 'One of them is a real obstacle, and it is worth solving before you choose a model.'
                             : (elec ? 'Nothing here stops you on those figures. ' + ampsStep()
                                     : 'Nothing here stops you.'));
    } else {
      /* one question, the next one */
      tail = 'The one thing still open is ' + article(missing[0].label) + '. ' +
             (elec ? 'Give me that and I can finish the usual figures for ' + cat.label + '. ' + ampsStep()
                   : 'Give me that and I can settle ' + cat.label + ' completely.');
    }
    return { kind: 'summary', category: cat.id, verdict: worst,
             settled: settled.length, missing: missing.length,
             html: lines.join('<br>') + '<br><br>' + tail,
             follows: followsFor(cat, settled[0].k.id) };
  }
  var ASKS_SUM = [' am i ok ', ' am i okay ', ' where am i ', ' where are we ',
                  ' what have i told you ', ' what do you know ', ' run it ',
                  ' run everything ', ' summary ', ' summarise ', ' summarize ',
                  ' recap ', ' so far ', ' all together ', ' altogether ',
                  ' is that everything ', ' anything else ', ' what else do you need ',
                  ' what do you still need ', ' overall '];
  function asksSummary(hay) { return hasAny(hay, ASKS_SUM); }

  var ASKS_FIT = [' what fits ', ' which ones fit ', ' what will fit ', ' what works ',
                  ' which fit ', ' what can i get ', ' what should i get ', ' recommend ',
                  ' suggestion ', ' which one ', ' show me what fits ', ' narrow it down ',
                  ' what is left ', ' whats left ', ' options for me ', ' what do you suggest '];
  function asksFit(hay) { return hasAny(hay, ASKS_FIT); }

  /* ---------- loading ---------- */
  function load(url) {
    if (KB) { return Promise.resolve(KB); }
    if (loading) { return loading; }
    loading = fetch(url, { credentials: 'omit' })
      .then(function (r) { if (!r.ok) { throw new Error('kb ' + r.status); } return r.json(); })
      .then(function (j) { KB = j; return KB; })
      .catch(function () { loading = null; return null; });
    return loading;
  }

  /* ---------- text ---------- */

  var STOP = { the:1, a:1, an:1, is:1, are:1, was:1, i:1, my:1, me:1, we:1, you:1, it:1, of:1, to:1, in:1, on:1, for:1, and:1, or:1, do:1, does:1, can:1, will:1, would:1, should:1, have:1, has:1, with:1, that:1, this:1, be:1, at:1, if:1, what:1, whats:1, how:1, about:1, get:1, got:1, need:1, want:1 };

  function hasAny(hay, list) {
    for (var i = 0; i < list.length; i++) { if (hay.indexOf(' ' + list[i] + ' ') > -1 || hay.indexOf(list[i]) > -1) { return list[i]; } }
    return null;
  }

  /* ---------- measurements ----------
     Understands 10ft, 10', 10 feet, 8'6", 8 ft 6 in, 4in, 4", 32 inch,
     30a, 30 amp, 240v, 1500w, 800lb. Returns every measurement found, in a
     normalised unit, so a constraint can pick the one it cares about. */
  /* People type "nine foot ceiling" and "thirty amp circuit" as readily as they
     type digits, and a figure she cannot parse is a question she cannot answer
     however much she knows. */

  /* "between 8 and 9 feet" and "9 to 10 ft" carry TWO figures, and only the
     second has a unit attached. Rewriting them so both are units gives the
     parser both, and pick() below then takes the more cautious of the two -
     which is the whole point of someone quoting a range rather than a number. */


  /* A figure only counts for a constraint if it is a figure that constraint
     could plausibly have. "my slab is 4 in" must never be read as a 4 in
     ceiling; the range is what stops a confident wrong answer, which is the
     only kind of answer that costs a customer money. */
  function plausible(k, v) {
    var r = k.plausible;
    if (!r) { return true; }
    return v >= r[0] && v <= r[1];
  }
  /* Someone who quotes a range is telling you they are not sure, and the
     honest answer is the one that holds at the worse end of it. So when more
     than one figure fits the same constraint, she answers the tighter band
     rather than the first one mentioned. */
  var CAUTION = { no: 0, tight: 1, most: 2, yes: 3 };
  function pick(list, k) {
    var best = null, bestRank = 99;
    for (var i = 0; i < list.length; i++) {
      if (list[i].unit !== k.unit || !plausible(k, list[i].value)) { continue; }
      var b = band(k, list[i].value);
      var rank = b ? (CAUTION[b.verdict] === undefined ? 2 : CAUTION[b.verdict]) : 2;
      if (rank < bestRank) { bestRank = rank; best = list[i].value; }
    }
    return best;
  }

  /* ---------- scoring ---------- */
  /* whole-word, so "lift" does not match "uplift" and a category can rule
     itself out of a phrase it would otherwise steal ("tail lift" is delivery,
     not a car lift). Returns the alias too, so the caller can spend it. */
  function word(hay, a) { return hay.indexOf(' ' + a + ' ') > -1; }

  /* ---------- saying you do not have one ----------

     "My garage has no dock, how does it get delivered" is a delivery question
     asked by somebody who has just told us they have no dock, and she was
     answering it with the dock hardware menu. The word that names a thing and
     the word that denies it are the same word; what separates them is what
     comes immediately before.

     So a name is only a name if it is not being negated. This reads the few
     words in front of the match, not the whole sentence, because "I have no
     dock but I want a hot tub" must still find the hot tub. */
  /* Every mention of this category in the sentence is a denial. */
  function allNegated(cat, hay) {
    var any = false, all = true;
    (cat.aliases || []).concat([cat.label]).forEach(function (a) {
      if (hay.indexOf(' ' + a + ' ') > -1) { any = true; if (!negated(hay, a)) { all = false; } }
    });
    return any && all;
  }
  var NEG = ['no', 'not', 'without', 'dont', 'doesnt', 'havent', 'hasnt', 'isnt',
             'never', 'lack', 'lacks', 'lacking', 'zero', 'none'];
  function negated(hay, phrase) {
    var at = hay.indexOf(' ' + phrase + ' ');
    if (at === -1) { return false; }   /* not said in so many words - not denied */
    while (at > -1) {
      var before = hay.slice(0, at).trim().split(' ');
      /* Three words back, and no further. "no dock", "without a dock",
         "I dont have a dock" all deny it; "I have no dock but I want a hot
         tub" must not deny the hot tub, and three words is what separates
         them. */
      var win = before.slice(-3);
      var clear = true, i, j;
      for (i = 0; i < win.length && clear; i++) {
        for (j = 0; j < NEG.length; j++) { if (win[i] === NEG[j]) { clear = false; break; } }
      }
      if (clear) { return false; }          /* one clean mention is enough */
      at = hay.indexOf(' ' + phrase + ' ', at + 1);
    }
    return true;
  }
  /* ---------- reading through typos ----------

     Someone standing in a garage typing on a phone with cold hands is exactly
     our customer. A matcher that only fires on correctly spelled words fails
     the person it was built for.

     Two rules make this safe rather than reckless:

     1. Only WORDS are fuzzed. Figures and units never are. "36" is never
        "35" and "240v" is never "120v" - a typo in prose is forgivable, a
        guessed number is a false claim, and this whole site is built on not
        making those.
     2. The distance allowed scales with length and never exceeds two. Short
        words are nearly exact; long words get a little room. Getting a
        confident wrong answer is worse than being asked to rephrase.

     FUZZED records what was corrected, so she can say what she understood the
     question to be - a wrong guess should be visible, not silent. */
  var FUZZED = null;

  /* Three or fewer letters are matched exactly - at that length almost any
     edit reaches a different real word. Four to six get one edit, seven and
     up get two. "lift" is four letters and "lfit" is the commonest way to
     mistype it, so four cannot be zero. */
  function tolerance(n) { return n <= 3 ? 0 : (n <= 6 ? 1 : 2); }

  /* Ordinary English words are NEVER treated as misspellings.

     This exists because of a real failure: "my clear ceiling height is 62 ft"
     was answered as a boat-lift question, because "clear" is one edit from
     "cleat", which is a dock-hardware alias. The customer had not made a
     mistake at all - the matcher invented one.

     The rule that prevents it is simple and general: only fuzz a word that is
     not already a word. A typo is a word that does not exist; correcting one
     that does is how a fuzzy matcher starts answering questions nobody asked. */
  /* A lookup table with no ancestry. See the note in vb-lang.js: a plain {}
     answers "constructor" with a function, and every table in this file is
     keyed on words a customer typed. */
  function bare(o) {
    var n = Object.create(null), k;
    for (k in o) { if (Object.prototype.hasOwnProperty.call(o, k)) { n[k] = o[k]; } }
    return n;
  }

  var REAL = Object.create(null);
  ('the be to of and a in that have i it for not on with he as you do at this but his by from they we say her she or an will my one all would there their what so up out if about who get which go me when make can like time no just him know take people into year your good some could them see other than then now look only come its over think also back after use two how our work first well way even new want because any these give day most us is are was were been has had did does made take made say said very much many still own see look come give need help take find tell ask seem feel try leave call room floor wall door side end part place case week month right left high low big small large long short old great same next last few own under above between through during before against without within along across behind beyond near far here there where why house home store price cost order item send ship set put keep hold turn move open close start stop run walk build fit clear cover check plan buy sell pay add level line size space area width height length depth weight power water heat cold warm hot air light dark hard soft full empty ready done sure fine good bad best worst wide narrow deep shallow thick thin heavy light tall wet dry loud quiet clean dirty safe cheap free real whole half twice each both every other another such more less again once often never always sometimes maybe perhaps around beyond inside outside upstairs downstairs outside garage basement kitchen garden yard shop office bay deck wire cable pipe drain vent duct panel breaker outlet switch pump fan motor frame base mount bolt anchor pad slab gravel concrete wood steel glass stone brick tile paint install fit deliver delivery return warranty service repair replace clean maintain week month year day night morning today tomorrow yesterday soon later early late stock stocked available ready order ordered shipping shipped arrive arrives arrived').split(' ').forEach(function (w) { REAL[w] = 1; });

  /* Damerau-Levenshtein, bounded - bails out as soon as it cannot win.

     The transposition case is the whole reason this is not plain Levenshtein.
     "cieling", "hieght", "waht", "teh" are all a single swap of adjacent
     letters, and they are the most common typos there are. Plain Levenshtein
     scores a swap as two edits, which puts every one of them out of reach. */
  function near(a, b, max) {
    if (a === b) { return 0; }
    var la = a.length, lb = b.length;
    if (Math.abs(la - lb) > max) { return max + 1; }
    var d = [], i, j;
    for (i = 0; i <= la; i++) { d[i] = [i]; }
    for (j = 0; j <= lb; j++) { d[0][j] = j; }
    for (i = 1; i <= la; i++) {
      var best = Infinity;
      for (j = 1; j <= lb; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        var v = Math.min(d[i][j - 1] + 1, d[i - 1][j] + 1, d[i - 1][j - 1] + cost);
        if (i > 1 && j > 1 &&
            a.charAt(i - 1) === b.charAt(j - 2) &&
            a.charAt(i - 2) === b.charAt(j - 1)) {
          v = Math.min(v, d[i - 2][j - 2] + 1);   /* one adjacent swap */
        }
        d[i][j] = v;
        if (v < best) { best = v; }
      }
      if (best > max) { return max + 1; }
    }
    return d[la][lb];
  }

  /* a phrase matches loosely if every one of its words is close to some run of
     words in the haystack, in order and adjacent */
  function looseHas(hay, phrase) {
    var target = phrase.split(' ').filter(Boolean);
    var hs = hay.trim().split(' ').filter(Boolean);
    if (!target.length || target.length > hs.length) { return false; }
    for (var start = 0; start + target.length <= hs.length; start++) {
      var ok = true, edits = 0;
      for (var k = 0; k < target.length; k++) {
        var t = target[k], h = hs[start + k];
        /* anything carrying a digit is matched exactly, never fuzzed */
        if (/[0-9]/.test(t) || /[0-9]/.test(h)) { if (t !== h) { ok = false; break; } continue; }
        if (t === h) { continue; }
        /* h is what the customer typed. If it is already a real word, they
           did not misspell anything and we must not pretend they did. */
        if (REAL[h]) { ok = false; break; }
        var tol = tolerance(t.length);
        if (tol === 0) { ok = false; break; }
        var d = near(t, h, tol);
        if (d > tol) { ok = false; break; }
        edits += d;
      }
      /* ONE character wrong in the whole phrase is a typo. Two is a different
         word: "rating" is two edits from "racking", and treating it as one
         made a rooftop-tent question come back about pallet racking. The
         tolerance function still allows two edits on a long word, but only one
         of them may be spent across the phrase as a whole. */
      if (ok && edits <= 1) { return true; }
    }
    return false;
  }

  function category(hay) {
    var best = null, bestLen = 0, bestAlias = null;
    (KB.categories || []).forEach(function (c) {
      var blocked = false;
      (c.exclude || []).forEach(function (x) { if (hay.indexOf(x) > -1) { blocked = true; } });
      if (blocked) { return; }
      (c.aliases || []).concat([c.label]).forEach(function (a) {
        if (word(hay, a) && !negated(hay, a) && a.length > bestLen) {
          best = c; bestLen = a.length; bestAlias = a;
        }
      });
    });
    if (best) { return { cat: best, alias: bestAlias }; }

    /* nothing matched cleanly - try again allowing for misspelling */
    (KB.categories || []).forEach(function (c) {
      var blocked = false;
      (c.exclude || []).forEach(function (x) { if (hay.indexOf(x) > -1) { blocked = true; } });
      if (blocked) { return; }
      (c.aliases || []).concat([c.label]).forEach(function (a) {
        if (a.length < 4) { return; }              /* too short to fuzz safely */
        if (looseHas(hay, a) && !negated(hay, a) && a.length > bestLen) {
          best = c; bestLen = a.length; bestAlias = a;
        }
      });
    });
    /* If a correction could point at more than one category, it is not a
       correction - it is a coin toss, and she does not toss coins. */
    var hits = {};
    (KB.categories || []).forEach(function (c) {
      (c.aliases || []).concat([c.label]).forEach(function (a) {
        if (a.length >= 4 && looseHas(hay, a)) { hits[c.id] = 1; }
      });
    });
    if (best && Object.keys(hits).length === 1) {
      FUZZED = bestAlias; return { cat: best, alias: bestAlias, loose: true };
    }
    return null;
  }
  /* ---------- two things at once ----------

     "Sauna or hot tub." "Hot tub vs swim spa." "Should I get a two post or a
     four post." Every one of these names two things, and she was answering
     about whichever one her matcher happened to rank first - which is the
     worst possible answer to a comparison, because it looks like an answer.

     A comparison is a different question from a question about one category,
     and it deserves a different shape: what each is FOR, and what each will
     ask of the room. The second half is the part nobody else will tell them,
     and it is the part she is uniquely able to say, because the constraints
     are already written down. */
  function categoriesIn(hay) {
    var found = [], seen = {};
    (KB.categories || []).forEach(function (c) {
      var blocked = false;
      (c.exclude || []).forEach(function (x) { if (hay.indexOf(x) > -1) { blocked = true; } });
      if (blocked) { return; }
      var best = null;
      (c.aliases || []).concat([c.label]).forEach(function (a) {
        if (word(hay, a) && !negated(hay, a) && (!best || a.length > best.length)) { best = a; }
      });
      if (best && !seen[c.id]) { seen[c.id] = 1; found.push({ cat: c, alias: best, at: hay.indexOf(' ' + best + ' ') }); }
    });
    found.sort(function (a, b2) { return a.at - b2.at; });
    /* Two categories claiming the SAME words are not two categories. "Gooseneck
       or bumper pull" is one trailer question: gooseneck and bumper pull are
       both trailer words, and dock hardware only got in because it owns the
       word bumper on its own. Where two matches overlap in the sentence, the
       longer phrase is the one the customer typed. */
    var keep = [];
    for (var i = 0; i < found.length; i++) {
      var f = found[i], beaten = false;
      for (var j = 0; j < found.length && !beaten; j++) {
        if (i === j) { continue; }
        var g = found[j];
        var fEnd = f.at + f.alias.length, gEnd = g.at + g.alias.length;
        var overlaps = f.at < gEnd && g.at < fEnd;
        if (overlaps && g.alias.length > f.alias.length) { beaten = true; }
      }
      if (!beaten) { keep.push(f); }
    }
    return keep;
  }
  /* The sentence has to be ASKING for a comparison. Two categories mentioned in
     passing - "I have a sauna, now I want a hot tub" - is not a comparison, it
     is a hot tub question with context, and answering it with a table would be
     answering something nobody asked. */
  function asksComparison(hay) {
    return hasAny(hay, [' or ', ' vs ', ' versus ', ' difference between ', ' compared to ',
                        ' compare ', ' rather than ', ' instead of ', ' better ', ' which one ',
                        ' which is ', ' between ',
                        /* two things in one place is a comparison too - they are
                           competing for the same room, the same panel or the
                           same budget, and that is exactly what she can settle */
                        ' both ', ' together ', ' alongside ', ' same room ', ' same space ',
                        ' same cafe ', ' same kitchen ', ' same garage ', ' same panel ',
                        ' same time ', ' as well as ']);
  }
  function whatItAsks(c) {
    var names = (c.constraints || []).map(function (k) { return k.label; });
    return names.length ? list(names) : null;
  }
  function comparison(cats, hay) {
    if (cats.length < 2) { return null; }
    var a = cats[0].cat, b2 = cats[1].cat;
    /* If somebody has written the comparison, use it - a written contrast beats
       an assembled one every time. */
    var written = (KB.comparisons || []).filter(function (x) {
      return (x.a === a.id && x.b === b2.id) || (x.a === b2.id && x.b === a.id);
    })[0];
    var parts = [];
    if (written) {
      parts.push(written.a === a.id ? written.text : written.text_reversed || written.text);
    }
    /* What each will ask of the room. This is the half nobody else tells them,
       and she has it written down already. */
    var qa = whatItAsks(a), qb = whatItAsks(b2);
    if (qa && qb) {
      /* "Saunas is decided by" - the labels are plural, so the sentence is
         built around them rather than in front of them. */
      parts.push('What decides <strong>' + a.label + '</strong> is ' + qa + '. ' +
                 'What decides <strong>' + b2.label + '</strong> is ' + qb + '.');
    }
    if (!parts.length) { return null; }
    parts.push('Tell me the room and I will run both against it, so you are choosing between two things that actually fit rather than two things on a page.');
    return { kind: 'comparison', category: a.id, against: b2.id,
             html: parts.join('<br><br>'),
             follows: followsFor(a, null) };
  }

  function constraintFor(cat, hay) {
    var best = null, bestLen = 0;
    (cat.constraints || []).forEach(function (k) {
      (k.aliases || []).concat([k.label]).forEach(function (a) {
        if ((a.indexOf(' ') > -1 ? hay.indexOf(a) > -1 : word(hay, a)) && a.length > bestLen) {
          best = k; bestLen = a.length;
        }
      });
    });
    if (best) { return best; }

    /* same tolerance as categories: "cieling hieght" and "amperge" must land.
       The FIGURE in the question is never fuzzed - only the word naming what
       the figure is about. */
    (cat.constraints || []).forEach(function (k) {
      (k.aliases || []).concat([k.label]).forEach(function (a) {
        if (a.length < 4) { return; }
        if (looseHas(hay, a) && a.length > bestLen) { best = k; bestLen = a.length; FUZZED = a; }
      });
    });
    return best;
  }
  /* no constraint named, but a figure given: if only one constraint in the
     category speaks that unit, she knows which one they mean */
  function byUnit(cat, unit) {
    return (cat.constraints || []).filter(function (k) { return k.unit === unit; });
  }
  function band(constraint, value) {
    var bands = constraint.bands || [];
    for (var i = 0; i < bands.length; i++) {
      var b = bands[i];
      if (b.lt === undefined && b.gt === undefined) { return b; }
      if (b.lt !== undefined && value < b.lt) { return b; }
      if (b.gt !== undefined && value > b.gt) { return b; }
    }
    return bands[bands.length - 1] || null;
  }
  /* Only a word that is ABOUT something earns the partial credit. A cue
     phrase is allowed to match in pieces - somebody rarely types the whole of
     "how much does it cost" - but the pieces that count are the pieces that
     carry the subject. Before this, "how much does it weigh" scored four
     points against that phrase on how, much, does and it, and got back an
     answer about price. Four words everybody types are worth nothing. */
  function contentOf(list) {
    return (L && L.content) ? L.content(list) : list;
  }
  /* She reads a word and its plural as the same word, the way the search
     field already did. "Am I towing it home or do you deliver" was reaching
     nothing, because the delivery entry is cued on "delivery" and the customer
     wrote "deliver". One language layer means one answer to what a word is. */
  function sameWord(a, b) {
    if (a === b) { return true; }
    return !!(L && L.stem && L.stem(a) === L.stem(b));
  }
  function inList(list, w) {
    for (var i = 0; i < list.length; i++) { if (sameWord(list[i], w)) { return true; } }
    return false;
  }
  function scoreEntry(entry, ws, hay) {
    var s = 0;
    var cws = contentOf(ws);
    /* A word of the customer's question is paid for ONCE, however many of the
       entry's cue phrases happen to contain it. Score is how much of the
       QUESTION an entry explains, not how often the entry repeats itself:
       "can you give me a discount code" was scoring four against the permits
       entry off the single word code, because four of its cue phrases mention
       building code. One word is one word. */
    var paid = {};
    (entry.q || []).forEach(function (phrase) {
      if (phrase.indexOf(' ') > -1) {
        /* A cue phrase made entirely of words everybody types - "how much",
           "do i need" - is not a cue. It matched every question in the shop.
           Whole-phrase credit is for a phrase that is about something. */
        var pc = contentOf(words(phrase));
        if (!pc.length) { return; }
        if (hay.indexOf(phrase) > -1) { s += 3; return; }
        pc.forEach(function (w) {
          if (inList(cws, w) && !paid[w]) { paid[w] = 1; s += 1; }
        });
        return;
      }
      /* a single-word cue that appears as a whole word is a strong signal:
         "hood" in a deck-oven question is not a coincidence */
      if (inList(ws, phrase)) { s += 2; }
    });
    return s;
  }
  /* Only what is actually present: a full phrase, or a single-word cue as a
     whole word. No credit for a phrase's individual words turning up apart. */
  function strictScore(entry, ws, hay) {
    var sc = 0;
    (entry.q || []).forEach(function (phrase) {
      if (phrase.indexOf(' ') > -1) { if (hay.indexOf(phrase) > -1) { sc += 3; } }
      else if (ws.indexOf(phrase) > -1) { sc += 2; }
    });
    return sc;
  }
  function bestStrict(pool, ws, hay) {
    var top = 0;
    (pool || []).forEach(function (e) { var v = strictScore(e, ws, hay); if (v > top) { top = v; } });
    return top;
  }
  function bestScored(pool, ws, hay) {
    var best = null, bestScore = 0;
    (pool || []).forEach(function (e) {
      var s = scoreEntry(e, ws, hay);
      if (s > bestScore) { bestScore = s; best = e; }
    });
    return { entry: best, score: bestScore };
  }
  function bestEntry(pool, ws, hay, floor) {
    var r = bestScored(pool, ws, hay);
    return r.score >= (floor || 2) ? r.entry : null;
  }

  /* ---------- the glossary ----------
     Someone who does not know the word cannot ask the question that reaches
     the answer, so she defines the vocabulary a specification sheet uses.

     It fires ONLY on a question that is asking what something means. That
     restriction is deliberate: several glossary terms are also constraint
     words - "amps", "psi" - and a definition returned where a figure was
     wanted would be a worse answer than the one it displaced. Constraints run
     first, and the definitional cue is what lets the rest through. */
  var DEFINE = [' what is ', ' what are ', ' whats ', " what's ", ' what does ', ' what do ',
                ' define ', ' definition ', ' meaning ', ' means ', ' mean ', ' stand for ',
                ' stands for ', ' explain ', ' difference between ', ' never heard of '];
  function asksDefinition(hay) { return hasAny(hay, DEFINE); }
  /* "What is a dedicated circuit" wants a definition. "What is the difference
     between GVWR and payload" wants the comparison, and the glossary can only
     ever define one of the two words in it. When the question is a COMPARISON
     and the category has an explainer written for exactly that comparison, the
     explainer answers and the glossary waits its turn. */
  var COMPARE = [' difference between ', ' vs ', ' versus ', ' compared to ',
                 ' or ', ' rather than ', ' instead of ', ' better than '];
  function comparingBeaten(cat, hay, ws) {
    if (!cat || !hasAny(hay, COMPARE)) { return false; }
    return bestScored(cat.explainers, ws, hay).score >= 3;
  }
  /* interrogatives that want prose rather than a verdict */
  var WHY = [' why ', ' how do ', ' how does ', ' how is ', ' how are ', ' how much ',
             ' how many ', ' difference between ', ' vs ', ' versus ', ' explain ',
             ' should i ', ' do i need ', ' what happens ', ' what type ', ' what kind ',
             ' which type ', ' which is better ', ' worth it '];
  function asksExplanation(hay) { return hasAny(hay, WHY); }
  function glossary(hay, ws) {
    var terms = KB.glossary || [];
    var best = null, bestLen = 0;
    for (var i = 0; i < terms.length; i++) {
      var al = terms[i].aliases || [];
      for (var j = 0; j < al.length; j++) {
        var a = al[j];
        /* words() drops anything under three letters, which is right for a
           matcher and wrong for a vocabulary: kW, LTL, RSC and psi are terms
           people ask about by name. A short alias is checked against the
           sentence itself, as a whole word. */
        var hit = a.indexOf(' ') > -1 ? (hay.indexOf(' ' + a + ' ') > -1)
                : (a.length > 2 ? (ws.indexOf(a) > -1) : (hay.indexOf(' ' + a + ' ') > -1));
        /* longest alias wins, so "dynamic load" beats "load" */
        if (hit && a.length > bestLen) { bestLen = a.length; best = terms[i]; }
      }
    }
    return best;
  }

  /* ---------- actions: the things she DOES, as opposed to knows ----------

     A third pool, kept apart from knowledge on purpose. A sentence is true or
     it is not; an action has a handler, a permission and a failure mode, and
     none of that belongs in a pool of sentences.

     Nothing here performs anything yet, and that is fine. What it does today
     is RECOGNISE the intent and decline it honestly, which is the whole point:
     "turn off the lights" must never fall through and be answered with a fact
     about saunas. Recognising a request and saying plainly that it is not
     built is an answer. Guessing is not.

     The host registers the doing. This file never learns how to fetch weather
     or reach a light - only that those are intents - so a speaker, a TV shell
     or a car head unit can each supply its own handler without this file
     changing at all. That separation is the reason the surfaces are cheap to
     add later, and expensive to retrofit if it is left until then. */
  var HANDLERS = Object.create(null);
  function registerAction(intent, fn) {
    if (intent && typeof fn === 'function') { HANDLERS[intent] = fn; }
  }
  function actionFor(hay, ws) {
    var best = null, bestScore = 0;
    (KB.actions || []).forEach(function (a) {
      var sc = strictScore(a, ws, hay);
      if (sc > bestScore) { bestScore = sc; best = a; }
    });
    return bestScore >= 2 ? best : null;
  }

  /* The spoken form of an answer.

     Her written answers are 40 to 80 words of dense prose carrying figures.
     That is right on a screen and unusable by ear - nobody retains "28 to 32
     inches covers most panel-built saunas" spoken aloud. So every answer also
     carries a spoken form: an entry may write its own as "s", and anything
     without one is reduced to its first sentences plus the offer of the rest.

     Deriving it is the fallback, not the goal. Writing "s" by hand is what
     makes her good out loud, and the convention exists NOW, while the file is
     small, precisely so that it is not a migration across thousands of entries
     later. */
  function speak(html) {
    var t = String(html || '')
      .replace(/<span class="vb-asst-defer">[\s\S]*?<\/span>/g, ' ')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s*&mdash;\s*/g, ', ').replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, 'and').replace(/&[a-z]+;/g, ' ')
      .replace(/\s+/g, ' ')
      /* a sentence assembled from parts leaves punctuation floating */
      .replace(/\s+([,.;:?!])/g, '$1').replace(/([,;:])\1+/g, '$1')
      .replace(/,\s*\./g, '.').replace(/\.\s*\./g, '.')
      .trim();
    if (t.length <= 200) { return t; }
    var cut = t.slice(0, 200);
    var i = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf('? '), cut.lastIndexOf('! '));
    if (i > 60) { return t.slice(0, i + 1) + ' Want the detail?'; }
    return cut.replace(/\s+\S*$/, '') + '. Want the detail?';
  }

  /* ---------- the answer ---------- */
  function ask(text) {
    /* ---------- a pronoun with nothing behind it ----------
       "Will it fit". "How wide is it". "How much does it weigh". Every one of
       these is a real question and none of them is answerable, because the
       only noun in the sentence is a word standing in for a noun nobody said.
       She was answering them anyway, from whichever aisle happened to share a
       word. A pronoun with no antecedent, and nothing else to go on, gets the
       one honest answer: she asks what "it" is.

       The guard is narrow on purpose. It needs BOTH an unbound pronoun and a
       near-empty sentence, so "my garage has no dock, how does it get
       delivered" - which carries garage, dock and delivered - still gets the
       freight answer it deserves. */
    var unbound = false;
    if (!LAST && KB) {
      var ph = ' ' + norm(text) + ' ';
      var bare = /( it | its | this | that | they | them | these | those | one )/.test(ph);
      var body = (L && L.content) ? L.content(words(ph)) : words(ph);
      unbound = bare && body.length < 2 && !category(ph);
    }
    var r = askInner(text);
    /* Nothing that depends on WHICH thing may be said about a thing nobody
       named. An answer that is true whatever "it" is - how delivery works,
       whether we install, what a word means - still stands, because it does
       not need to know. */
    if (r && unbound && r.kind !== 'general' && r.kind !== 'action' && r.kind !== 'glossary') {
      r = null;
    }
    /* Nothing category-specific may be said about a category nobody named.
       "How tall does it need to be" is not a rooftop tent question because
       tents have a height, and "how much does it weigh" is not a pricing
       question. Silence costs a phone call; a confident wrong answer about a
       doorway costs a freight return. */
    /* One exception, and it is not a loosening.

       Step F deduces a category when exactly one of them can EXPLAIN the
       question - "what holds the columns down" names no lift, and only the
       lift knows about columns. When the answer that comes back is that same
       explainer, matched on a whole phrase rather than a shared word, the
       deduction and the answer are the same piece of evidence, and killing it
       leaves her silent about the one thing in the shop that fits. She still
       may not state a FACT or a VERDICT about a category nobody named - those
       are claims about their room, and this is not. */
    if (r && INFERRED && r.kind === 'explainer' && r.certain) { /* allowed */ }
    else if (r && INFERRED && (r.kind === 'fact' || r.kind === 'explainer' ||
                          r.kind === 'constraint' || r.kind === 'history')) { r = null; }
    /* Set or clear what she is waiting on. Only a request for a figure sets
       it; any other answer means the thread moved on. */
    if (r && r.kind === 'need-figure') { PEND = { cat: r.category, k: r.constraint }; }
    else if (r) { PEND = null; }
    /* Hold a figure that nothing could be done with; drop it once it has been
       used, or once she has said something that settles the same ground. */
    if (r && (r.kind === 'constraint' || r.kind === 'which')) { HELD = null; }
    else {
      var hm = measures(text);
      if (hm.length) { HELD = { ms: hm, hay: ' ' + norm(text) + ' ' }; }
    }
    /* If the only reason this matched was a spelling correction, say so. A
       wrong guess must be visible rather than silent. */
    if (r && FUZZED) { r.read_as = FUZZED; }
    /* ---------- how sure she is ----------
       Every answer says how it was arrived at, because "the doorway rules it
       out" and "I think you mean saunas, and if so the doorway rules it out"
       are different sentences and only one of them is honest about a guess.

         firm    - the customer named the subject and gave the figure
         read    - it only matched after a spelling was corrected
         deduced - nobody named a category; she worked out which one
         general - true whatever they are shopping for
    */
    if (r) {
      r.confidence = r.read_as ? 'read'
                   : (INFERRED || r.loose) ? 'deduced'
                   : (r.kind === 'general' || r.kind === 'glossary' || r.kind === 'action') ? 'general'
                   : 'firm';
      /* A deduced answer names the assumption in the answer itself, not in a
         tooltip. Somebody who is actually asking about something else needs to
         see the mistake before they act on the figure. */
      if (r.confidence === 'deduced' && r.category) {
        var dc = catById(r.category);
        if (dc) { r.assumed = dc.label; }
      }
    }
    /* Everything she decided is decided by here. What is left is how it gets
       said, and that belongs to one file so the register can never drift
       between one kind of answer and another. */
    /* An electrical figure against a category's usual range is a range, not a
       match for a model (1 Nov 2026, his direction): the voice leads it as a
       range, and the answer ends with the step that does match a model. */
    if (r && r.kind === 'constraint' && !r.voiced) {
      if (r.heard && r.heard.unit === 'amps') { r.range = true; r.amps = true; }
      if (r.range) { r.amps = true; }
      if (r.amps && r.html) {
        var at = r.html.indexOf(' <span class="vb-asst-defer">');
        r.html = at < 0 ? r.html + ' ' + ampsStep() : r.html.slice(0, at) + ' ' + ampsStep() + r.html.slice(at);
      }
    }
    if (r) {
      var V = (typeof VB_VOICE !== 'undefined' && VB_VOICE) ? VB_VOICE
            : (typeof require === 'function' ? require('./vb-voice.js') : null);
      if (V) { V.dress(r); }
    }
    if (r && !r.spoken) { r.spoken = speak(r.html); }
    /* the spoken reply is shortened; an electrical range still ends on the step that matches a model */
    if (r && r.amps && r.spoken && r.spoken.indexOf(ampsStep()) < 0) { r.spoken = r.spoken + ' ' + ampsStep(); }
    /* ---------- the gap, if there was one ----------
       Recorded after everything else has had its turn, so what is logged is
       what the customer actually got. A silence is the most valuable line in
       the log; a guess is the second most valuable, because a guess that was
       wrong looks exactly like a guess that was right from in here. */
    /* Tell the rest of the shop what the customer has told us. The field ranks
       with it; nothing is stored and nothing leaves the page. */
    var CX = (typeof VB_CONTEXT !== 'undefined' && VB_CONTEXT) ? VB_CONTEXT : null;
    if (CX) { CX.tell(noted(), LAST ? LAST.id : null); }
    var LN = (typeof VB_LEARN !== 'undefined' && VB_LEARN) ? VB_LEARN : null;
    if (LN) {
      if (!r) { LN.record('assistant', 'silence', text, null); }
      else if (r.read_as) { LN.record('assistant', 'corrected', text, { cat: r.category, corrected: r.read_as }); }
      else if (INFERRED || r.loose) { LN.record('assistant', 'guessed', text, { cat: r.category }); }
      else if (r.kind === 'action' && !r.performed) { LN.record('assistant', 'refused', text, null); }
    }
    return r;
  }

  var FILLER = bare({ about: 1, roughly: 1, around: 1, approx: 1, approximately: 1, its: 1, it: 1,
                 is: 1, im: 1, i: 1, have: 1, got: 1, a: 1, an: 1, the: 1, just: 1, over: 1,
                 under: 1, nearly: 1, maybe: 1, say: 1, and: 1, ft: 1, feet: 1, foot: 1, in: 1,
                 inch: 1, inches: 1, amp: 1, amps: 1, lb: 1, lbs: 1, pounds: 1, v: 1, volts: 1,
                 mine: 1, ours: 1, my: 1, of: 1, to: 1, up: 1 });
  function catById(id) {
    var cs = (KB && KB.categories) || [];
    for (var i = 0; i < cs.length; i++) { if (cs[i].id === id) { return cs[i]; } }
    return null;
  }
  function conById(cat, id) {
    var ks = (cat && cat.constraints) || [];
    for (var i = 0; i < ks.length; i++) { if (ks[i].id === id) { return ks[i]; } }
    return null;
  }

  /* A greeting is shorter than the shortest word she reads. words() drops
     anything under three letters, which is right everywhere except here: "hi"
     is a whole sentence. So courtesy is matched on the raw text, before the
     filtering that would throw it away. */
  function courtesy(hay) {
    var acts = KB.actions || [];
    for (var i = 0; i < acts.length; i++) {
      if (acts[i].id !== 'courtesy') { continue; }
      var qs = acts[i].q || [];
      for (var j = 0; j < qs.length; j++) {
        if (hay.trim() === qs[j] || hay.indexOf(' ' + qs[j] + ' ') > -1) { return acts[i]; }
      }
    }
    return null;
  }
  function askInner(text) {
    if (!KB || !text) { return null; }
    FUZZED = null;
    /* Cleared HERE, at the top of every turn, not two hundred lines down.

       It used to be reset in the middle of the matcher, which is fine for any
       turn that reaches the middle - and three branches return before it:
       courtesy, an answer to a pending figure, and a comparison. So a turn
       that deduced a category left INFERRED set, and the NEXT turn inherited
       it. "Sauna or hot tub" - where the customer named both - came back
       captioned "Taking that as a question about saunas", and was filed in the
       gap log as a guess. Even "hi" came back marked deduced. */
    INFERRED = false;
    var hay = norm(text);
    observe(hay);
    observeSupply(text, hay);
    /* only when courtesy is the WHOLE message - "hi, will a lift fit" is a
       question with a hello on the front, and the question is the point */
    if (words(hay).length === 0) {
      var cy = courtesy(hay);
      if (cy) {
        return { kind: 'action', intent: cy.intent, capable: true, performed: false,
                 html: cy.a, spoken: cy.s || null };
      }
    }

    /* An answer to the figure she asked for. Only when the reply is mostly
       just that figure - "9 foot", "about 240 amps", "7'6" - because a longer
       sentence carrying a number is a new question that happens to have one
       in it. */
    if (PEND) {
      /* Declared here as well as below, because this branch returns before the
         function body reaches the other one. It was appending the string
         "undefined" to every answer she gave to her own question. */
      var pdefer = KB.deference || '';
      var pms = measures(text);
      /* She asked "how wide is the doorway?" - so "28" means 28 inches. A
         bare number carries no unit, and until now that made the answer to
         her own question unreadable. The pending constraint supplies it. */
      if (!pms.length) {
        var only = String(text).replace(/[^0-9.\/ -]/g, ' ').trim();
        var pn = parseFloat(only);
        if (only && !isNaN(pn) && /^[\d.\s-]+$/.test(only)) {
          var pc0 = catById(PEND.cat), pk0 = pc0 && conById(pc0, PEND.k);
          if (pk0) { pms = [{ unit: pk0.unit, value: pn }]; }
        }
      }
      if (pms.length) {
        var bare = String(text).toLowerCase().replace(/[^a-z]+/g, ' ').trim().split(/\s+/)
                     .filter(function (w) { return w && !FILLER[w]; });
        if (bare.length <= 2) {
          var pc = catById(PEND.cat), pk = pc && conById(pc, PEND.k);
          if (pk) {
            var pv = pick(pms, pk);
            if (pv !== null) {
              var pb = band(pk, pv);
              if (pb) {
                note(pk, pv);
                return { kind: 'constraint', category: pc.id, verdict: pb.verdict,
                         heard: { value: pv, unit: pk.unit, label: pk.label },
                         html: pb.text + ' <span class="vb-asst-defer">' + pdefer + '</span>',
                         follows: followsFor(pc, pk.id) };
              }
            }
          }
        }
      }
    }
    /* Two categories and a comparison cue: that is a different question. */
    if (asksComparison(hay)) {
      var pair = categoriesIn(hay);
      if (pair.length >= 2) {
        var cmp = comparison(pair, hay);
        if (cmp) { LAST = pair[0].cat; return cmp; }
      }
    }
    var found = category(hay);
    /* Her matcher and the search field's had different ideas about a typo.
       When hers finds nothing, the field's scorer gets a turn against the same
       alias table - which is how "hot tob electrical" reaches hot tubs. Only a
       decisive score counts, and it is marked loose so it can never carry the
       authority of a category the customer actually named. */
    /* A fallback needs something to fall back ON. The field's scorer will
       happily rank the whole shop against "hi", because when every word of a
       query is filler it scores the filler - which is how a greeting became
       an offer of ice machines. No content word, no guess. */
    var hasWord = L && L.content ? L.content(words(hay)).length > 0 : true;
    if (!found && SHELF && hasWord) {
      var FF = FINDER();
      if (FF) {
        var bestC = null, bestS = 0, bestName = null, bestNameS = 0;
        for (var ci = 0; ci < SHELF.c.length; ci++) {
          var cr = SHELF.c[ci];
          var hnd = String(cr[0]).replace(/-/g, ' ');
          var sName = FF.scoreFields([[cr[1], 1], [hnd, 0.9]], text);
          var sAlias = FF.scoreFields([[cr[5] || '', 0.85]], text);
          /* What counts as naming a category you did not name.

             Misspelling the NAME is naming it: "hot tob" is hot tubs. But the
             alias bag is fifty words long, and one of them turning up is a
             coincidence, not a request - "what about the power" was becoming a
             home gym question because power rack is on that list. So an alias
             match has to carry at least two of the customer's own words before
             it is allowed to speak for a category. */
          /* Two of the customer's words, SPELLED, not guessed. A fuzzy hit
             plus a generic one is not two words: "towing it home" was being
             read as a golf simulator question because home appears in
             "driving range at home". */
          var land = FF.hits((cr[1] || '') + ' ' + hnd + ' ' + (cr[5] || ''), text, 4);
          /* The NAME outranks the alias bag outright, not on points. "hot tob
             electrical" scores against the sauna aliases on two words and
             against the words HOT TUB on a one-letter slip, and the slip is
             the answer: people misspell the name of the thing they came for.
             A tier, not a tie-break - an alias can never outbid a name. */
          if (sName >= 2) {
            if (bestName === null || sName > bestNameS) { bestNameS = sName; bestName = cr; }
            continue;
          }
          var sc = (sAlias >= 2 && land >= 2) ? sAlias : 0;
          if (sc > bestS) { bestS = sc; bestC = cr; }
        }
        if (bestName) { bestC = bestName; }
        if (bestC) {
          var kc = catById(bestC[0]);
          /* the same denial rule the named pass uses - a category the customer
             has just told us they do NOT have cannot be the subject */
          if (kc && !allNegated(kc, hay)) { found = { cat: kc, loose: true }; }
        }
      }
    }
    var cat = found ? found.cat : null;

    /* An action is checked only when nobody NAMED a category, so "the sauna
       lights" stays a sauna question and never becomes a request to switch
       something off. Checked here, before the subject of the last sentence is
       inherited, so a conversation about saunas cannot swallow "what is the
       weather".

       A LOOSE category match does not count as naming one. "Lock the front
       door" was being read as dock hardware, because the fuzzy pass is happy
       to turn lock into dock, and a spelling guess must not outrank a request
       the customer stated plainly. */
    if (!found || found.loose) {
      var act = actionFor(hay, words(hay));
      if (act) {
        var h = act.capable ? HANDLERS[act.intent] : null;
        if (h) {
          var done = h(text, act);
          if (done) { return done; }
        }
        return { kind: 'action', intent: act.intent, capable: !!act.capable,
                 performed: false, html: act.a, spoken: act.s || null };
      }
    }

    /* A question that asks what a WORD means is answered by the glossary,
       before anything else gets a chance. "What is a dedicated circuit" was
       being answered with a general note about wiring, and "explain three
       phase to me" with one about supply - both true, neither a definition.
       Checked after actions so "what is the weather" is still the weather. */
    /* A question about what is ON the shelf, answered from the shelf. */
    /* "Which ones fit" is a shelf question when she knows nothing about your
       room and a FIT question the moment she does. Listing three products to
       somebody who has just told you their circuit, without checking it, is
       the same failure as not listening. */
    if (SHELF && asksShelf(hay) && !(asksFit(hay) && noted().length)
        && (L && L.content ? L.content(words(hay)).length > 0 : true)) {
      var sa = shelfAnswer(text, hay);
      if (sa) { return sa; }
    }

    if (asksDefinition(hay)) {
      /* "What is a dedicated circuit" wants a definition. "What is the
         difference between GVWR and payload" wants the comparison, and the
         glossary can only ever define one of the two words in it. So when the
         question is a COMPARISON and the category has an explainer written for
         exactly that comparison, the explainer answers and the glossary waits
         its turn below. */
      /* A comparison naming no category at all. "What is the difference between
         dynamic and static roof load" belongs to rooftop tents and says so
         nowhere - the only thing in the shop that knows BOTH halves of the
         question is the explainer written for it. When exactly one category can
         explain a comparison, on a whole phrase, that is not a guess: it is the
         single specific answer, and a definition of one of the two words is the
         weaker one. */
      if (!cat && hasAny(hay, COMPARE)) {
        var cws0 = words(hay), top0 = null, s0 = 0, sec0 = 0;
        (KB.categories || []).forEach(function (c) {
          var sc = bestScored(c.explainers, cws0, hay).score;
          if (sc > s0) { sec0 = s0; s0 = sc; top0 = c; }
          else if (sc > sec0) { sec0 = sc; }
        });
        if (top0 && s0 >= 4 && s0 > sec0) {
          var xr1 = bestScored(top0.explainers, cws0, hay);
          if (xr1.entry) {
            LAST = top0;
            return { kind: 'explainer', category: top0.id, topic: xr1.entry.id,
                     html: xr1.entry.a, certain: true, follows: followsFor(top0, 'explainer') };
          }
        }
      }
      /* The same rule one level out. "What is the difference between threshold
         and white glove" is a comparison of two delivery levels, and the
         glossary can define one of them; the general answer describes all four
         and the relationship between them. A definition of half the question
         is the weaker answer wherever the better one lives. */
      var gWs = words(hay);
      var generalBeats = hasAny(hay, COMPARE) &&
                         bestScored(KB.general, gWs, hay).score >= 4;
      if (!generalBeats && !comparingBeaten(cat, hay, gWs)) {
        var gEarly = glossary(hay, words(hay));
        if (gEarly) {
          return { kind: 'glossary', term: gEarly.t, html: gEarly.a,
                   follows: followsFor(cat, 'glossary') };
        }
      }
    }

    var inferred = false;      /* true when nobody named the category, she deduced it */
    INFERRED = false;
    if (!cat && LAST) {
      /* A continuation carries the subject if it brings a figure, names one of
         that subject's constraints, or asks about something that subject can
         explain. The third case is what makes "why does it need a vapour
         barrier" work as a second sentence - it names no category and no
         figure, and before this it fell through to the general knowledge and
         got an answer about freight. */
      var carries = measures(text).length > 0
                 || !!constraintFor(LAST, hay)
                 /* "So what fits?" is the question a verdict creates. It names
                    nothing, carries no figure, and is unmistakably about the
                    thing they were just talking about. */
                 || asksFit(hay) || asksSummary(hay)
                 || bestScored(LAST.explainers, words(hay), hay).score >= 2;
      if (carries) { cat = LAST; }
    }

    /* F. Nobody said a category and there is no subject to inherit - but the
       question may still be unmistakably about one. "what holds the columns
       down" names no lift; only the lift knows about columns. Adopt a category
       when exactly one of them can explain the question and no other comes
       close, which keeps this from guessing. */
    if (!cat) {
      var hay0 = norm(text), ws0 = words(hay0);
      var top = null, topScore = 0, second = 0;
      (KB.categories || []).forEach(function (c) {
        var sc = bestStrict(c.explainers, ws0, hay0);
        if (sc > topScore) { second = topScore; topScore = sc; top = c; }
        else if (sc > second) { second = sc; }
      });
      /* a whole phrase, or one unambiguous word that no other category claims */
      if (top && (topScore >= 3 || (topScore >= 2 && second === 0))) {
        cat = top; LAST = top; inferred = true; INFERRED = true;
      }
    }
    if (found) { LAST = found.cat; }
    /* spend the category's own words so they cannot also be read as a
       constraint - "electric fireplace" must not look like an electrical
       question */
    var rawHay = hay;                 /* before the category's words are spent */
    /* Spending the alias must not spend a CONSTRAINT.

       "home gym floor" matched the category alias "home gym floor" - the whole
       sentence - and left her nothing to read, so a question with an obvious
       figure behind it came back as an essay about rubber matting. The alias
       and the constraint were fighting over the same word.

       So a word the alias shares with one of that category's constraints
       survives being spent. The category still cannot be read as its own
       constraint - "electric fireplace" is not an electrical question, because
       electric is not a constraint word there - but "floor" stays on the table
       for the thing that actually knows what to do with it. */
    if (found) {
      var keep = {};
      (cat.constraints || []).forEach(function (k) {
        (k.aliases || []).concat([k.label]).forEach(function (a) {
          a.split(' ').forEach(function (w) { if (w) { keep[w] = 1; } });
        });
      });
      /* ...unless the word is part of what the category IS CALLED. "electric"
         is a constraint word in that aisle and also half the name of the
         product, and a name is never a question. */
      var own = {};
      (norm(cat.label) + ' ' + String(cat.id).replace(/-/g, ' ')).split(' ')
        .forEach(function (w) { if (w) { own[w] = 1; } });
      var kept = String(found.alias).split(' ').filter(function (w) { return keep[w] && !own[w]; });
      hay = hay.split(' ' + found.alias + ' ').join(kept.length ? ' ' + kept.join(' ') + ' ' : ' ');
    }
    var ws = words(hay);
    var rawWs = words(rawHay);
    var wantsHistory = !!hasAny(hay, KB.history_triggers || []);
    /* Somebody who says "my client" or "our restaurant" has told you they do
       not need the paragraph written for a first-time buyer. Talking down to a
       contractor is its own kind of rudeness, so the note at the end of a
       verdict gets shorter rather than disappearing - the caveat is still
       true, it just does not need explaining twice. */
    var deference = (TRADE && KB.deference_trade) ? KB.deference_trade : (KB.deference || '');

    /* 1. history, and only when asked for */
    if (wantsHistory && cat) {
      var h = (cat.history || [])[0];
      if (h) {
        return { kind: 'history', category: cat.id, html: h.a,
                 follows: followsFor(cat, 'history') };
      }
    }

    /* 1a. "Am I okay?" - the running total, said out loud. */
    if (cat && !inferred && asksSummary(hay)) {
      var rs = roomSummary(cat);
      if (rs) { return rs; }
    }

    /* 1b. "So what fits?" - the question a verdict creates.
       Checked before the constraint pass, because somebody asking what fits is
       not offering a new figure and should not be asked for one. */
    if (cat && !inferred && asksFit(hay)) {
      var fa = fitsAnswer(cat);
      if (fa) { return fa; }
    }

    /* 2. a constraint with a figure attached - the thing she is actually for.

       A verdict is the most authoritative thing she ever says. "Under 8 ft
       rules out overhead pressing" is a sentence someone acts on, so it may
       only be said about a category the customer NAMED, or one carried in from
       the sentence before. Never about one deduced at step F.

       "I have a 7 foot ceiling and a 30 inch doorway, what fits" is the case
       this exists for: two real figures, no product named at all, and she was
       deducing home gyms from the word ceiling and then ruling things out with
       total confidence. The question is not answerable without knowing what
       they are shopping for, and saying so is worth more than a verdict about
       equipment nobody mentioned. */
    if (cat && !inferred) {
      var k = constraintFor(cat, hay);
      var ms = measures(text);
      /* Nothing measured in this sentence, but a figure is being held from an
         earlier one. The earlier SENTENCE says which constraint it was about -
         "my ceiling is nine and a half feet" names the ceiling - so the naming
         of a category is all that was missing. */
      if (!ms.length && HELD) {
        var hk = constraintFor(cat, HELD.hay);
        if (hk && pick(HELD.ms, hk) !== null) { k = hk; ms = HELD.ms; }
      }
      if (k) {
        var v = pick(ms, k);
        if (v !== null) {
          var b = band(k, v);
          if (b) {
            note(k, v);
            var sideNote = aside(k);
            return { kind: 'constraint', category: cat.id, verdict: b.verdict,
                     heard: { value: v, unit: k.unit, label: k.label },
                     setting: SETTING ? SETTING.id : null, trade: TRADE,
                     html: b.text + (sideNote ? ' ' + sideNote : '') +
                           ' <span class="vb-asst-defer">' + deference + '</span>',
                     follows: followsFor(cat, k.id) };
          }
        }
        /* The constraint is named but no number given. Before asking for one,
           check whether the question was never about a figure: "why does the
           slab matter for a car lift" names the slab and wants an explanation.
           An explainer that matches strongly wins; otherwise she asks. */
        /* A sentence that opens by asking why or how is not asking for a
           number, so a weaker explainer match is still the better answer than
           a request for a figure that was never coming. */
        /* "what is gvwr" names a constraint word and wants a definition, not a
           number. The glossary answers it; asking for a figure does not. */
        if (asksDefinition(rawHay) && !comparingBeaten(cat, rawHay, rawWs)) {
          var glx = glossary(rawHay, rawWs);
          if (glx) {
            return { kind: 'glossary', term: glx.t, html: glx.a, follows: followsFor(cat, 'glossary') };
          }
        }
        var xr0 = bestScored(cat.explainers, rawWs, rawHay);
        /* Naming a constraint flatly - "saunas doorway" - is asking for the
           number that decides it, not for an essay about it. An explainer only
           wins here if the sentence actually asked why or how. */
        if (asksExplanation(rawHay) && xr0.score >= 2) {
          return { kind: 'explainer', category: cat.id, topic: xr0.entry.id, html: xr0.entry.a,
                   follows: followsFor(cat, 'explainer') };
        }
        /* Before asking, check whether she was already told. Somebody who has
           said their ceiling is 7 ft has said it, and asking again is the
           clearest possible sign that nobody was listening. */
        var had = recall(k);
        if (had) {
          var hb = band(k, had.value);
          if (hb) {
            note(k, had.value);
            /* No figure playback here: the sentence already opens with "you
               told me 28 inches", and playing it back as well means two
               answers in one conversation start with the same six words. */
            return { kind: 'constraint', category: cat.id, verdict: hb.verdict,
                     recalled: true, range: k.unit === 'amps',
                     html: 'You told me ' + fig(had.value, k.unit) + ', so \u2014 ' +
                           lower(hb.text) + ' <span class="vb-asst-defer">' + deference + '</span>',
                     follows: followsFor(cat, k.id) };
          }
        }
        return { kind: 'need-figure', category: cat.id, constraint: k.id,
                 html: 'What is ' + article(k.label) +
                       '? Give it to me however you have it &mdash; ' +
                       (k.unit === 'ft' ? 'feet and inches are both fine' :
                        k.unit === 'in' ? 'inches or feet, either way' :
                        k.unit === 'amps' ? 'the breaker size, or the voltage if that is what you know' :
                        'whatever you have') + '.',
                 follows: followsFor(cat, k.id) };
      }
    }

    /* 2b. a figure with no constraint named. If one constraint in the
       category speaks that unit, she knows what they mean; if several, she
       asks which rather than guessing.

       Skipped entirely when the category was inferred rather than named: an
       unlabelled figure against a deduced subject is two guesses stacked, and
       that is exactly the shape of a confident wrong answer. */
    if (cat && !inferred) {
      var ms2 = measures(text);

      /* People give two figures at once - "9 ft ceiling and a 4 in slab" - and
         answering only the first is the kind of half-answer that sends someone
         to the phone. Resolve every figure that lands on a DIFFERENT constraint
         and answer them together, in the order they were said. */
      if (ms2.length > 1) {
        var hits = [], usedIds = {};
        for (var mj = 0; mj < ms2.length; mj++) {
          var cj = byUnit(cat, ms2[mj].unit).filter(function (c) { return plausible(c, ms2[mj].value); });
          if (cj.length !== 1 || usedIds[cj[0].id]) { continue; }
          var bj = band(cj[0], ms2[mj].value);
          if (bj) { usedIds[cj[0].id] = 1; hits.push({ k: cj[0], b: bj }); }
        }
        if (hits.length > 1) {
          var parts = hits.map(function (h) {
            return '<strong>' + h.k.label.charAt(0).toUpperCase() + h.k.label.slice(1) +
                   '.</strong> ' + h.b.text;
          });
          var worst = hits.filter(function (h) { return h.b.verdict === 'tight'; }).length
                      ? 'tight' : hits[0].b.verdict;
          var ampsAt = hits.filter(function (h) { return h.k.unit === 'amps'; }).length;
          return { kind: 'constraint', category: cat.id, verdict: worst,
                   range: ampsAt === hits.length, mixed: ampsAt > 0 && ampsAt < hits.length, amps: ampsAt > 0,
                   html: parts.join('<br><br>') +
                         ' <span class="vb-asst-defer">' + deference + '</span>',
                   follows: followsFor(cat, hits[0].k.id) };
        }
      }

      for (var mi = 0; mi < ms2.length; mi++) {
        var cands = byUnit(cat, ms2[mi].unit).filter(function (c) { return plausible(c, ms2[mi].value); });
        if (cands.length === 1) {
          var b2 = band(cands[0], ms2[mi].value);
          if (b2) {
            note(cands[0], ms2[mi].value);
            return { kind: 'constraint', category: cat.id, verdict: b2.verdict,
                     heard: { value: ms2[mi].value, unit: cands[0].unit, label: cands[0].label },
                     html: b2.text + ' <span class="vb-asst-defer">' + deference + '</span>',
                     follows: followsFor(cat, cands[0].id) };
          }
        } else if (cands.length > 1) {
          return { kind: 'which', category: cat.id,
                   html: 'Is that the ' + list(cands.map(function (c) { return c.label; }), 'or') +
                         '? Tell me which and I will answer it.',
                   follows: followsFor(cat, null) };
        }
      }
    }

    /* 2c. "can I wire it myself" is not a question about the equipment, it is a
       question about who is permitted to do the work - and answering it with
       heater sizing, which is what she used to do, is a worse answer than
       saying plainly that it is licensed work. */
    if (hasAny(hay, [' myself ', ' my self ', ' diy ', ' on my own ', ' by myself '])
        && hasAny(hay, [' can i ', ' could i ', ' should i ', ' am i able ', ' allowed '])) {
      var who = (KB.general || []).filter(function (e) { return e.id === 'who-installs'; })[0];
      if (who) {
        return { kind: 'general', html: who.a, follows: followsFor(cat, 'general') };
      }
    }

    /* 2d. An INFERRED category is a guess. A general answer is not.

       This was the most damaging failure she had. Nobody names a category in
       "how long does delivery take" or "do i need a permit", so step F deduces
       one from a weak signal - and then step 3 answered from it with the same
       confidence it uses for a category the customer actually said. "How long
       does delivery take" came back with a safe's fire rating. "Where are you
       located" came back with generator clearances. Both read as authoritative
       and both were nonsense.

       So when the category was deduced rather than stated, the general pool is
       asked first. If the question is answerable about buying heavy goods in
       general, that answer is true whichever category she guessed, and it
       cannot be embarrassingly wrong. Only if the general pool declines does
       she fall through to the guessed category, at a higher bar. */
    if (cat && inferred) {
      var gFirst = bestEntry(KB.general, ws, hay, 2);
      if (gFirst) {
        return { kind: 'general', category: null, html: gFirst.a,
                 follows: followsFor(cat, 'general') };
      }
    }

    /* 3. what she knows about the category beyond its figures.

       Two pools, and they are WEIGHED rather than ordered. Facts answer what;
       explainers answer why and how - "why does a sauna need a vapour barrier"
       is not a figure and not a fact, and before the explainers existed it fell
       straight through to a refusal. Ordering one pool ahead of the other would
       just move which kind of question gets the worse answer, so whichever
       matches the question more strongly wins. */
    if (cat) {
      /* A named category earns the normal bar. A deduced one has to clear a
         whole phrase rather than one shared word, because one shared word is
         how "the room is 300 cm by 400 cm" became an answer about wine
         cabinets. */
      var bar = inferred ? 4 : 2;
      var fr = bestScored(cat.facts, rawWs, rawHay);
      var xr = bestScored(cat.explainers, rawWs, rawHay);
      if (xr.score >= bar && xr.score >= fr.score) {
        return { kind: 'explainer', category: cat.id, topic: xr.entry.id, html: xr.entry.a,
                 certain: xr.score >= bar,
                 follows: followsFor(cat, 'explainer') };
      }
      if (fr.score >= bar) {
        /* A fact about a category NOBODY NAMED is a confident answer to a
           question that was never asked. "How tall does it need to be" is not
           a rooftop tent question just because tents have a height. */
        if (inferred) { return null; }
        return { kind: 'fact', category: cat.id, html: fr.entry.a, follows: followsFor(cat, 'fact') };
      }
    }

    /* 3b. a word rather than a question. Only when the sentence is asking what
       something means - see the note on the glossary above. And still not when
       the question is a COMPARISON that something else can answer whole: the
       glossary can define white glove, and the general answer describes all
       four delivery levels and how they differ, which is the question. */
    if (asksDefinition(hay) &&
        !(hasAny(hay, COMPARE) && bestScored(KB.general, ws, hay).score >= 4)) {
      var gl = glossary(hay, ws);
      if (gl) {
        return { kind: 'glossary', term: gl.t, html: gl.a, follows: followsFor(cat, 'glossary') };
      }
    }

    /* 4. something general about buying heavy goods here */
    var g = bestEntry(KB.general, ws, hay, 2);
    /* A general answer given while she knows which aisle she is standing in
       still says which aisle. The answer is true everywhere - that is what
       makes it general - but the surface needs the category to offer the road
       to it, and "what can my truck actually pull" is a trailers question
       whichever sentence answers it. */
    /* The category is stamped only when it was NAMED. A general answer is true
       whatever the subject, so stamping a guessed subject on it costs nothing
       to the answer and everything to the surface that reads it: "can you
       deliver to a second floor apartment" was coming back as a question about
       safes, because floor safe is on that list. */
    var stamp = (found && !found.loose && !inferred) ? cat.id : null;
    if (g) { return { kind: 'general', category: stamp, html: g.a,
                      follows: followsFor(cat, 'general') }; }

    /* 4b. the glossary again, now without requiring the definitional phrasing.
       By this point the constraints, the facts, the explainers and the general
       knowledge have all declined the question, so a bare term is very unlikely
       to be displacing a better answer, and a definition beats a refusal. */
    var gl2 = glossary(hay, ws);
    if (gl2) {
      return { kind: 'glossary', term: gl2.t, html: gl2.a, follows: followsFor(cat, 'glossary') };
    }

    /* 5. a BARE category with no question attached - offer what she has on it.

       This step used to fire for anything that merely named a category, which
       was right when there was no model behind her: an offer beats "I do not
       know". With a model it is actively wrong - it swallowed
       "what do most people get wrong when buying a car lift" and answered with
       a list of constraints, because the question named a category and matched
       no band. Exactly the long tail the model exists for.

       So it now fires only when the input really is bare. Once the category's
       own words are spent, anything left over means the person asked something,
       and something asked belongs to the model. With no model configured the
       old behaviour stands, because then an offer genuinely is the best answer
       available. */
    /* A DEDUCED category may not offer a menu. The offer asserts the subject -
       "on pallet racking I can work through..." - and she deduced the subject
       from one word. "How much does it weigh" was being answered with a pallet
       racking menu because racking is the only aisle whose explainers mention
       weight. If everything else has declined and the category was never
       named, the honest answer is that she does not know what "it" is. */
    /* Naming a new category when she already holds figures about the room.

       "Saunas, my ceiling is seven feet" ... "what about car lifts". The room
       has not changed. Offering a blank menu of things to measure, to somebody
       who has just measured one of them, is the behaviour of a form rather
       than a person - so she spends what she was told first, and asks only for
       what is actually still missing. */
    if (cat && !INFERRED && noted().length) {
      var settled = [], missing = [];
      (cat.constraints || []).forEach(function (k) {
        var f = recall(k);
        if (f) {
          var fb = band(k, f.value);
          if (fb) { settled.push({ k: k, f: f, b: fb }); return; }
        }
        missing.push(k);
      });
      if (settled.length) {
        var lines = settled.map(function (h) {
          return '<strong>' + capital(h.k.label) + ' at ' + fig(h.f.value, h.k.unit) + ':</strong> ' +
                 lower(h.b.text);
        });
        var tailQ = missing.length
          ? 'What I still need is ' + list(missing.map(function (k) { return k.label; })) + '.'
          : 'That is everything I measure on ' + cat.label + '.';
        var worst = 'yes';
        settled.forEach(function (h) {
          if (h.b.verdict === 'no') { worst = 'no'; }
          else if (h.b.verdict === 'tight' && worst !== 'no') { worst = 'tight'; }
        });
        settled.forEach(function (h) { note(h.k, h.f.value); });
        var ampsSet = settled.filter(function (h) { return h.k.unit === 'amps'; }).length;
        return { kind: 'constraint', category: cat.id, verdict: worst, recalled: true,
                 range: ampsSet === settled.length, mixed: ampsSet > 0 && ampsSet < settled.length, amps: ampsSet > 0,
                 html: 'You have already told me enough to settle part of this. ' +
                       lines.join('<br><br>') + '<br><br>' + tailQ +
                       ' <span class="vb-asst-defer">' + deference + '</span>',
                 follows: followsFor(cat, settled[0].k.id) };
      }
    }

    if (cat && !INFERRED) {
      /* ws is already stop-word filtered by words(), so what is left is
         whatever the person actually asked beyond naming the category. */
      var leftover = ws;
      /* an explainer that matched at all means this was a question, not a
         bare category - hand it to the model rather than offering a menu */
      if (bestScored(cat.explainers, rawWs, rawHay).score >= 2) { return null; }
      var hasModel = !!(window.VB_ASSISTANT && window.VB_ASSISTANT.endpoint);
      /* With a model behind her, ANY leftover word means a question was asked
         and the question belongs to the model. Without one, a menu still beats
         a refusal, so the old latitude stands. */
      var bare = hasModel ? leftover.length === 0 : leftover.length <= 2;
      if (bare || !hasModel) {
        var names = (cat.constraints || []).map(function (c) { return c.label; });
        if (names.length) {
          return { kind: 'offer', category: cat.id,
                   html: 'On ' + cat.label + ' I can work through ' + list(names) +
                         '. Tell me what you are working with and I will tell you what clears it.',
                   follows: followsFor(cat, 'offer') };
        }
      }
    }

    return null;   /* she says she does not know, and hands over a person */
  }

  /* Some labels already carry their own article - "the electrical supply" -
     and "What is the the electrical supply" is the kind of thing he notices. */
  function article(label) {
    return /^the\s/i.test(label) ? label : 'the ' + label;
  }
  function list(a, join) {
    if (a.length === 1) { return a[0]; }
    return a.slice(0, -1).join(', ') + ' ' + (join || 'and') + ' ' + a[a.length - 1];
  }

  /* follow-ups drawn from the same file, so they can only suggest things she
     can actually answer */
  /* ---------- what she offers next ----------

     These are the chips under an answer, and they were a fixed list: the same
     three constraints in the same order, every time, including the one the
     customer had just answered. A conversation where the assistant keeps
     offering you a question you already answered is a conversation with
     somebody who is not listening.

     So the offers are now built from where the conversation actually is:

       - anything already TOLD is dropped, not re-asked.
       - what is left is offered nearest-first, so the next tap is the next
         thing she needs rather than an arbitrary one.
       - when every figure is in, she stops asking for figures and offers the
         two questions that come after them: what fits, and where it goes.
       - a category she is only guessing at offers nothing, because a chip is a
         suggestion and suggesting the wrong aisle is worse than suggesting
         nothing.
  */
  function followsFor(cat, exclude) {
    var out = [];
    if (!cat) { return out; }
    var open = [];
    (cat.constraints || []).forEach(function (k) {
      if (k.id === exclude) { return; }
      if (recall(k)) { return; }        /* already told - never ask twice */
      open.push(k);
    });
    open.forEach(function (k) {
      if (out.length >= 3) { return; }
      var ex = exampleFor(k);
      if (ex) { out.push(ex); }
    });
    /* Everything measured. The conversation has somewhere else to go. */
    if (!open.length && noted().length) {
      out.push(['So what fits?', null]);
      out.push(['How does it get delivered?', null]);
      out.push(['What do people get wrong with ' + cat.label + '?', null]);
    }
    if (out.length < 3 && (cat.facts || []).length) {
      var f = cat.facts[0];
      if (f && f.q && f.q[0]) { out.push([capital(f.q[0]) + ' for ' + cat.label + '?', null]); }
    }
    return out.slice(0, 3);
  }
  function exampleFor(k) {
    var u = k.unit;
    if (u === 'ft') { return ['My ' + k.label + ' is 10 ft. Does that work?', 'My ' + k.label + ' is <b>10 ft</b>. Does that work?']; }
    if (u === 'in') { return ['My ' + k.label + ' is 32 in. Is that enough?', 'My ' + k.label + ' is <b>32 in</b>. Is that enough?']; }
    if (u === 'amps') { return ['I have a 30 amp circuit. Is that enough?', 'I have a <b>30 amp</b> circuit. Is that enough?']; }
    if (u === 'lb') { return ['What about ' + k.label + '?', null]; }
    return ['What about ' + k.label + '?', null];
  }
  function capital(s) { return s.charAt(0).toUpperCase() + s.slice(1); }
  function lower(s) {
    /* only the first letter, and only when the second word is not a name or a
       figure - "30 A and above" must not become "30 a and above" */
    var t = String(s || '');
    if (/^[A-Z][a-z]/.test(t)) { return t.charAt(0).toLowerCase() + t.slice(1); }
    return t;
  }
  function fig(v, unit) {
    var V = (typeof VB_VOICE !== 'undefined' && VB_VOICE) ? VB_VOICE
          : (typeof require === 'function' ? require('./vb-voice.js') : null);
    if (V && V.figure) { return V.figure(v, unit); }
    return String(v) + (unit ? ' ' + unit : '');
  }

  return { load: load, ask: ask, registerAction: registerAction, speak: speak, shelf: shelf,
           reset: function () {
             LAST = null; PEND = null; HELD = null; INFERRED = false; FUZZED = null;
             NOTED = { figures: Object.create(null), order: [] };
             SUPPLY = { v: null, a: null, three: false, outlet: false };
             SETTING = null; TRADE = false;
             var CX0 = (typeof VB_CONTEXT !== 'undefined' && VB_CONTEXT) ? VB_CONTEXT : null;
             if (CX0) { CX0.forget(); }
             var V = (typeof VB_VOICE !== 'undefined' && VB_VOICE) ? VB_VOICE
                   : (typeof require === 'function' ? require('./vb-voice.js') : null);
             if (V) { V.reset(); }
           },
           /* Reading it returns the current subject. Passing an id SETS it,
              which is what lets a conversation restored from another page keep
              the thread it was on - without it she would forget mid-sentence
              what "does that work?" referred to. */
           subject: function (id) {
             if (typeof id !== 'undefined') {
               LAST = null;
               if (KB && id) {
                 var cs = KB.categories || [];
                 for (var i = 0; i < cs.length; i++) {
                   if (cs[i].id === id) { LAST = cs[i]; break; }
                 }
               }
             }
             return LAST ? LAST.id : null;
           },
           /* The nearest aisle to something she could not answer, offered as a
              QUESTION rather than as an answer. Used by the handoff: "I do not
              know that one - it sounds closest to hot tubs, is that what you
              are looking at?" is a far better dead end than a phone number
              alone, and it is honest because it asks rather than asserts. */
           nearest: (function () {
             /* Built once, from the shelf: how many aisles use each word. A
                word used by more than one aisle cannot point at any of them. */
             var OWN = null;
             OWN_RESET = function () { OWN = null; };
             function build() {
               OWN = Object.create(null);
               for (var i = 0; i < SHELF.c.length; i++) {
                 var cr = SHELF.c[i];
                 var bag = norm(cr[1] + ' ' + String(cr[0]).replace(/-/g, ' ') + ' ' + (cr[5] || ''));
                 var seen = {}, ws2 = bag.split(' ');
                 for (var j = 0; j < ws2.length; j++) {
                   var w = ws2[j];
                   if (!w || w.length < 4 || seen[w]) { continue; }
                   seen[w] = 1;
                   OWN[w] = OWN[w] ? OWN[w] + 1 : 1;
                 }
               }
             }
             /* Words that name a shape rather than a thing. Unique to one aisle
                by accident of vocabulary, and useless as evidence: "hammock
                stand" reached home gyms because squat STAND is on that list. */
             var GENERIC = bare({ stand: 1, stands: 1, unit: 1, units: 1, system: 1, systems: 1,
                             set: 1, sets: 1, kit: 1, kits: 1, box: 1, case: 1, cover: 1,
                             covers: 1, mount: 1, mounts: 1, part: 1, parts: 1, base: 1,
                             top: 1, tops: 1, door: 1, doors: 1, panel: 1, panels: 1,
                             line: 1, lines: 1, size: 1, model: 1, models: 1, piece: 1,
                             frame: 1, frames: 1, stock: 1, item: 1, items: 1, thing: 1 });
             function ownWord(cr, text) {
               if (!OWN) { build(); }
               var hay1 = ' ' + norm(text) + ' ';
               /* A whole multi-word name the customer typed is the strongest
                  evidence there is, and it survives words that are individually
                  too short to count: "hot tub" is two three-letter words. */
               var kc = catById(cr[0]);
               if (kc) {
                 var als = (kc.aliases || []).concat([kc.label]);
                 for (var a = 0; a < als.length; a++) {
                   if (als[a].indexOf(' ') > -1 && hay1.indexOf(' ' + als[a] + ' ') > -1) { return true; }
                 }
               }
               var bag = ' ' + norm(cr[1] + ' ' + String(cr[0]).replace(/-/g, ' ') + ' ' + (cr[5] || '')) + ' ';
               var qs = words(hay1);
               for (var i = 0; i < qs.length; i++) {
                 var w = qs[i];
                 if (w.length < 4 || GENERIC[w]) { continue; }
                 var hit = bag.indexOf(' ' + w + ' ') > -1
                        || bag.indexOf(' ' + (L && L.stem ? L.stem(w) : w) + ' ') > -1;
                 if (hit && OWN[w] === 1) { return true; }
               }
               return false;
             }
             return function (text) {
             if (!KB || !SHELF || !text) { return null; }
             var FF = FINDER();
             if (!FF) { return null; }
             /* A suggestion needs a real word to be a suggestion rather than a
                guess. "How much does it weigh" has exactly one content word
                and pointing it at an aisle would be inventing a subject the
                customer never named - which is the thing she was built not to
                do. */
             var hay0 = ' ' + norm(text) + ' ';
             if (/( it | its | this | that | they | them | these | those )/.test(hay0)) {
               var body0 = (L && L.content) ? L.content(words(hay0)) : words(hay0);
               if (body0.length < 2) { return null; }
             }
             var best = null, bestS = 0;
             for (var i = 0; i < SHELF.c.length; i++) {
               var cr = SHELF.c[i];
               var hnd = String(cr[0]).replace(/-/g, ' ');
               /* at least one of the customer's words as a WHOLE word in this
                  aisle's vocabulary, and a word that belongs to this aisle
                  alone. A prefix is not enough - "hammock stand" was reaching
                  standby generators because stand opens standby - and a shared
                  word is not enough either: "washing machine" found ice
                  machines on the word machine, which two aisles use. */
               if (!ownWord(cr, text)) { continue; }
               /* one word may go unanswered here, and only here: this is a
                  guess at the nearest aisle, not a filter, and "steam showers"
                  should reach saunas on the word steam alone. The hit check
                  above is what keeps that from becoming a free-for-all. */
               var s = Math.max(FF.scoreFields([[cr[1], 1], [hnd, 0.9]], text, 1),
                                FF.scoreFields([[cr[5] || '', 0.7]], text, 1));
               if (s > bestS) { bestS = s; best = cr; }
             }
             return (best && bestS >= 1.2) ? { id: best[0], name: best[1] } : null;
             };
           }()),
           ready: function () { return !!KB; },
           version: function () { return KB ? KB.version : null; } };
})();
